/**
 * Painters for the isometric world: terrain, crops, props, buildings and machines.
 * Each returns SVG markup strings; the map component assembles and sorts them back to
 * front by `x + y`. Ported from the design handoff under D148; every number is the
 * handoff's own.
 *
 * The look follows the reference world illustrations: buildings with real height and
 * gambrel roofs, post-and-rail fences round every pasture, animals with legs and heads,
 * and crops drawn as individual plants in rows rather than hatching.
 *
 * D149 is the one departure. The handoff paints a parcel as a rectangle; a field here is
 * any connected set of tiles, so every pass works on the union of a parcel's tiles: the
 * fill and the outline are the union's own rings, the drill rows are clipped to the x
 * spans the union covers in each row, and a scatter is rejected back onto the union rather
 * than spread over a bounding box. The rectangle is the case where that outline has four
 * corners, and it takes a fast path.
 */
import {
  type AnimalKind,
  type Bounds,
  type BuildingUse,
  type IsoParcel,
  type MachineRun,
  type Palette,
  type Point,
  type TileKey,
  PLAN_TILE,
  TH,
  TW,
  turnWorld,
  alpha,
  cropKey,
  depth,
  READY_LIFT,
  frontCorner,
  inUnion,
  isPlan,
  laneRuns,
  mix,
  polyPath,
  project,
  rectPts,
  rnd,
  machineRun,
  rowSpans,
  scatter,
  screenFacing,
  shade,
  stepBack,
  tint,
  towardEye,
  parcelFill,
  parcelBoundaries,
  parseTileKey,
  soilOpacity,
  unionArea,
  unionBounds,
  unionPath,
  unionStrokes,
} from './iso.ts';
import {
  ANIMAL_SCALE,
  ASSET_PALETTE,
  SNOW,
  COUNTRY_TREE,
  type CountryTreeKind,
  BARN_ROOF,
  DENSITY,
  buildingDecor,
  FENCE_DECOR,
  FENCE_HEIGHT,
  FENCE_SHAPE,
  workerLook,
  SILO,
  MARK_SCALE,
  BALE,
  BREEDS,
  BUILDING_HEIGHT,
  BURST,
  READY_BADGE,
  CHAIN_ROOF,
  CHIMNEY_BRICK_SHADE,
  CROCUS,
  CROP_PARTS,
  FISH,
  GLASSHOUSE,
  HEDGE,
  HEN,
  HIVE,
  HOP,
  MARK,
  MEMORIAL,
  monumentGround,
  type MemorialForm,
  MEADOW,
  PLAIN_ANIMAL,
  PLANT,
  PLANT_STEP,
  PUDDLE,
  ROOT_LEAF,
  SHADOW,
  SMOKE,
  STYLE_WALL,
  TIER_SIGN,
  TREE,
  VINE,
  WORKER,
  breedLook,
  breedOf,
  cropDensity,
  styleOf,
  type Breed,
  type BuildingStyle,
} from '../idle/appearance.ts';
import { TRACK, TRACK_SURFACE } from '../idle/appearance.ts';
import { THEME_MAP_COLOURS } from '../idle/assets/idle-worlds-assets.js';
import { M as MOTOR } from './iso-kit-motorsport-art.ts';
import { ROADS } from './iso-kit-arena-art.ts';

/* D284: the looks both renderers share, kept reachable from here for the flat map's callers. */
export { ANIMAL_SCALE, MARK_SCALE } from '../idle/appearance.ts';
export { BREEDS, breedOf, styleOf, type BuildingStyle };

/* Materials come off the palette, which resolves them from the --map-* token layer, so
   the map re-themes with the rest of the system instead of carrying its own hex. */

export function isoBox(
  x: number,
  y: number,
  w: number,
  h: number,
  ht: number,
  top: string,
  left: string,
  right: string,
  o: string[],
): void {
  /* D178: from above a box is its top and nothing else. There is no side to see, so the
     two walls are left out rather than drawn with no depth in them. */
  if (isPlan()) {
    o.push(`<path d="${polyPath(rectPts(x, y, w, h))}" fill="${top}"/>`);
    return;
  }
  const up = rectPts(x, y, w, h, ht) as [Point, Point, Point, Point];
  const dn = rectPts(x, y, w, h, 0) as [Point, Point, Point, Point];
  /* D176: the two walls the viewer can see are the two that meet at the corner nearest the
     eye, and which corner that is changes with the turn. The ring A, B, C, D keeps its
     winding under a quarter turn, so the neighbour one step on round it is always the wall
     running away to the left of the screen and the one step back is always the right. At
     rest the front corner is C and this is the pair the box has always drawn. The light is
     fixed to the screen (D176), so left stays the shaded side at every orientation. */
  const f = frontCorner(x, y, w, h);
  const l = up[(f + 1) % 4] as Point,
    r = up[(f + 3) % 4] as Point;
  const lb = dn[(f + 1) % 4] as Point,
    rb = dn[(f + 3) % 4] as Point;
  const fu = up[f] as Point,
    fd = dn[f] as Point;
  o.push(`<path d="${polyPath([l, fu, fd, lb])}" fill="${left}"/>`);
  o.push(`<path d="${polyPath([fu, r, rb, fd])}" fill="${right}"/>`);
  o.push(`<path d="${polyPath(up)}" fill="${top}"/>`);
}

/* ---------- crops as plants ---------- */

/**
 * D269: carrots and potatoes in their own hand, so neither is ever read as wheat. Carrots
 * are low feathery fronds in a deep blue-green with orange shoulders showing when ripe;
 * potatoes are round bushes that flower white and lilac and show tubers when ripe.
 */
function paintRootPlant(
  cx: number,
  cy: number,
  kind: 'carrot' | 'potato',
  stage: number,
  o: string[],
): void {
  const f = (n: number) => n.toFixed(1);
  const leaf = ROOT_LEAF[kind === 'carrot' ? 'carrots' : 'potatoes'] as string;
  if (isPlan()) {
    o.push(`<circle cx="${f(cx)}" cy="${f(cy)}" r="${f(1 + stage * 0.4)}" fill="${leaf}"/>`);
    if (stage >= 5)
      o.push(
        `<circle cx="${f(cx)}" cy="${f(cy)}" r="0.9" fill="${kind === 'carrot' ? CROP_PARTS.carrot : CROP_PARTS.tuberPlan}"/>`,
      );
    return;
  }
  if (kind === 'carrot') {
    const h = 2 + stage * 1.5;
    if (stage >= 4)
      o.push(
        `<ellipse cx="${f(cx)}" cy="${f(cy - 0.6)}" rx="1.3" ry="0.9" fill="${CROP_PARTS.carrot}"/>`,
      );
    o.push(
      `<path d="M${f(cx)} ${f(cy - 0.8)} l-2.6 ${f(-h * 0.8)} M${f(cx)} ${f(cy - 0.8)} l-0.8 ${f(-h)} M${f(cx)} ${f(cy - 0.8)} l1 ${f(-h)} M${f(cx)} ${f(cy - 0.8)} l2.8 ${f(-h * 0.75)}" stroke="${leaf}" stroke-width="0.8" fill="none" stroke-linecap="round"/>`,
    );
    return;
  }
  const r = 1.4 + stage * 0.55;
  o.push(
    `<ellipse cx="${f(cx)}" cy="${f(cy - r * 0.8)}" rx="${f(r * 1.2)}" ry="${f(r)}" fill="${leaf}"/>`,
  );
  o.push(
    `<ellipse cx="${f(cx - r * 0.4)}" cy="${f(cy - r * 1.2)}" rx="${f(r * 0.6)}" ry="${f(r * 0.45)}" fill="${tint(leaf, 0.22)}"/>`,
  );
  if (stage >= 3)
    o.push(
      `<circle cx="${f(cx + r * 0.3)}" cy="${f(cy - r * 1.7)}" r="0.8" fill="${CROP_PARTS.potatoFlower[0]}"/><circle cx="${f(cx - r * 0.6)}" cy="${f(cy - r * 1.5)}" r="0.7" fill="${CROP_PARTS.potatoFlower[1]}"/>`,
    );
  if (stage >= 5)
    o.push(
      `<ellipse cx="${f(cx + r)}" cy="${f(cy + 0.2)}" rx="1.1" ry="0.8" fill="${CROP_PARTS.tuber}"/>`,
    );
}

/**
 * D300: strawberries, a low ground bush that never stands up: a white flower from stage 3,
 * red berries showing at the foot of the leaves once ripe.
 */
function paintBerryPlant(cx: number, cy: number, stage: number, fill: string, o: string[]): void {
  const f = (n: number) => n.toFixed(1);
  const leaf = tint(fill, 0.12);
  const r = 1.2 + stage * 0.5;
  o.push(
    `<ellipse cx="${f(cx)}" cy="${f(cy)}" rx="${f(r * 1.3)}" ry="${f(r * 0.9)}" fill="${leaf}"/>`,
  );
  if (stage >= 3)
    o.push(
      `<circle cx="${f(cx - r * 0.5)}" cy="${f(cy - (isPlan() ? 0 : r * 0.5))}" r="0.7" fill="${CROP_PARTS.berryFlower}"/>`,
    );
  if (stage >= 5)
    for (const fx of [0.5, -0.1])
      o.push(
        `<circle cx="${f(cx + r * fx)}" cy="${f(cy + (isPlan() ? r * 0.3 : -r * 0.1))}" r="0.8" fill="${CROP_PARTS.berry}"/>`,
      );
}

function plant(cx: number, cy: number, kind: string, stage: number, fill: string, o: string[]) {
  const h = 3 + stage * 2.4;
  const stem = shade(fill, 0.34),
    leaf = tint(fill, 0.12);
  if (kind === 'carrot' || kind === 'potato') {
    paintRootPlant(cx, cy, kind, stage, o);
    return;
  }
  if (kind === 'berry') {
    paintBerryPlant(cx, cy, stage, fill, o);
    return;
  }
  /* D178: from above a plant is what its top looks like — a rosette on the row, growing
     with the stage. No stem, because a stem seen from above is a point. */
  if (isPlan()) {
    const r = 1.1 + stage * 0.42;
    o.push(
      /* D269: a grain's ripe head is its own hue lifted, not one shared gold, so barley
         never ripens the colour of wheat. */
      `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${r.toFixed(1)}" fill="${kind === 'grain' && stage >= 5 ? tint(fill, 0.55) : leaf}"/>`,
    );
    if (kind === 'ball' || kind === 'tall')
      o.push(
        `<circle cx="${(cx - r * 0.3).toFixed(1)}" cy="${(cy - r * 0.3).toFixed(1)}" r="${(r * 0.45).toFixed(1)}" fill="${tint(fill, 0.3)}"/>`,
      );
    if (kind === 'flower' && stage >= 3)
      o.push(
        `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${(r * 0.6).toFixed(1)}" fill="${CROP_PARTS.rapeFlower}"/>`,
      );
    return;
  }
  if (kind === 'ball') {
    o.push(
      `<circle cx="${cx.toFixed(1)}" cy="${(cy - h * 0.4).toFixed(1)}" r="${(1.6 + stage * 0.7).toFixed(1)}" fill="${leaf}"/>`,
    );
    o.push(
      `<circle cx="${(cx - 0.6).toFixed(1)}" cy="${(cy - h * 0.5).toFixed(1)}" r="${(0.9 + stage * 0.35).toFixed(1)}" fill="${tint(fill, 0.3)}"/>`,
    );
    return;
  }
  o.push(
    `<path d="M${cx.toFixed(1)} ${cy.toFixed(1)} l0 ${(-h).toFixed(1)}" stroke="${stem}" stroke-width="1.1" stroke-linecap="round"/>`,
  );
  if (kind === 'grain') {
    o.push(
      `<ellipse cx="${cx.toFixed(1)}" cy="${(cy - h - 1).toFixed(1)}" rx="1.1" ry="${(1.6 + stage * 0.3).toFixed(1)}" fill="${stage >= 5 ? tint(fill, 0.55) : leaf}"/>`,
    );
  } else if (kind === 'tall') {
    o.push(
      `<path d="M${cx.toFixed(1)} ${(cy - h * 0.5).toFixed(1)} l-2.6 -2 M${cx.toFixed(1)} ${(cy - h * 0.75).toFixed(1)} l2.6 -2" stroke="${leaf}" stroke-width="1.1" fill="none" stroke-linecap="round"/>`,
    );
  } else if (kind === 'flower') {
    o.push(
      `<circle cx="${cx.toFixed(1)}" cy="${(cy - h - 0.5).toFixed(1)}" r="1.5" fill="${stage >= 3 ? CROP_PARTS.rapeFlower : leaf}"/>`,
    );
  } else {
    o.push(
      `<path d="M${cx.toFixed(1)} ${(cy - h * 0.6).toFixed(1)} l-2.4 -2.4 M${cx.toFixed(1)} ${(cy - h * 0.6).toFixed(1)} l0 -3.2 M${cx.toFixed(1)} ${(cy - h * 0.6).toFixed(1)} l2.4 -2.4" stroke="${leaf}" stroke-width="1.1" fill="none" stroke-linecap="round"/>`,
    );
  }
}

/** One line per x span the union covers in this row, so nothing is drawn off the field. */
function rowLines(tiles: Iterable<TileKey>, y: number, inset = 0): string[] {
  const out: string[] = [];
  for (const [x0, x1] of rowSpans(tiles, y)) {
    if (x1 - x0 <= inset * 2) continue;
    const [ax, ay] = project(x0 + inset, y),
      [bx, by] = project(x1 - inset, y);
    out.push(
      `<line x1="${ax.toFixed(1)}" y1="${ay.toFixed(1)}" x2="${bx.toFixed(1)}" y2="${by.toFixed(1)}"/>`,
    );
  }
  return out;
}

export function paintTexture(p: IsoParcel, P: Palette, fill: string, o: string[]): void {
  const dark = alpha(shade(fill, 0.55), 0.3),
    light = alpha(tint(fill, 0.7), 0.22);
  const b = unionBounds(p.tiles);
  const area = unionArea(p.tiles);
  if (!b.w) return;
  // D440: under winter's snow every field rests: bare rows, no plants.
  const stage = P.snow ? 0 : (p.stage ?? 0);
  if (p.t === 'field') {
    /* Drill rows first, so the plants stand in them. */
    const step = stage === 0 ? 0.26 : 0.38;
    const g: string[] = [];
    for (let i = step; i < b.h; i += step) g.push(...rowLines(p.tiles, b.y + i));
    o.push(`<g stroke="${dark}" stroke-width="${stage === 0 ? 1.6 : 1}">${g.join('')}</g>`);
    if (stage === 0) {
      const g2: string[] = [];
      for (let i = step; i < b.h; i += step) g2.push(...rowLines(p.tiles, b.y + i - 0.07));
      o.push(`<g stroke="${light}" stroke-width="1">${g2.join('')}</g>`);
      return;
    }
    /* Individual plants, in the rows, front to back. */
    const kind = PLANT[p.crop ?? ''] || PLANT[cropKey(p.crop)] || 'tuft';
    /* D268: each milestone reached plants the rows closer, up to half as far apart again. */
    const dense = cropDensity(p.tier);
    const sy = PLANT_STEP.y / dense,
      sx = PLANT_STEP.x / dense;
    const rows: Point[] = [];
    for (let ry = sy / 2; ry < b.h; ry += sy)
      for (let rx = sx / 2; rx < b.w; rx += sx) {
        if (inUnion(p.tiles, b.x + rx, b.y + ry)) rows.push([rx, ry]);
      }
    /* Back to front at this turn, not down the rows: at two of the four orientations the
       field is read from the other end and row order would paint the near plants first. */
    rows.sort(
      (a, c) => depth(b.x + a[0], b.y + a[1]) - depth(b.x + c[0], b.y + c[1]) || a[0] - c[0],
    );
    const buf: string[] = [];
    rows.forEach(([rx, ry]) => {
      const [cx, cy] = project(b.x + rx, b.y + ry);
      plant(cx, cy, kind, stage, fill, buf);
    });
    o.push(buf.join(''));
  } else if (p.t === 'pasture' || p.t === 'meadow') {
    const r = rnd(b.x * 23 + b.y * 11),
      tufts: string[] = [];
    for (const [wx, wy] of scatter(p.tiles, r, Math.round(area * MEADOW.tuftsPerTile), 0.12)) {
      const [cx, cy] = project(wx, wy);
      /* From above a tuft splays round its own point rather than standing up from it. */
      const arms = isPlan()
        ? 'l-2.4 -1.4 M0 0 l0 2.8 M0 0 l2.4 -1.4'
        : 'l-2 -3.2 M0 0 l0 -4 M0 0 l2 -3.2';
      const at = `${cx.toFixed(1)} ${cy.toFixed(1)}`;
      tufts.push(`<path d="M${at} ${arms.replace(/M0 0/g, `M${at}`)}"/>`);
    }
    o.push(
      `<g stroke="${alpha(shade(fill, MEADOW.tuftShade), 0.38)}" stroke-width=".9" stroke-linecap="round" fill="none">${tufts.join('')}</g>`,
    );
    if (p.t === 'meadow') {
      const r2 = rnd(b.x * 5 + 3),
        fl: string[] = [];
      const cols: readonly string[] = P.S.frost ? [MEADOW.frostFlower] : MEADOW.flowers;
      scatter(p.tiles, r2, Math.round(area * MEADOW.flowersPerTile), 0.2).forEach(([wx, wy], i) => {
        const [cx, cy] = project(wx, wy);
        fl.push(
          `<circle cx="${cx.toFixed(1)}" cy="${(cy - (isPlan() ? 0 : 3.4)).toFixed(1)}" r="1.5" fill="${cols[i % cols.length]}"/>`,
        );
      });
      o.push(fl.join(''));
    }
  } else if (p.t === 'yard') {
    const r = rnd(99),
      sp: string[] = [];
    for (const [wx, wy] of scatter(p.tiles, r, 130)) {
      const [cx, cy] = project(wx, wy);
      sp.push(
        `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${(0.6 + r() * 0.9).toFixed(1)}"/>`,
      );
    }
    o.push(`<g fill="${alpha(shade(fill, 0.5), 0.24)}">${sp.join('')}</g>`);
  } else if (p.t === 'pond') {
    /* D301: a trout pond or raceway, not a flat tile: dark channel lines with a lighter
       ripple between them, so the water reads as moving. */
    for (let i = 1; i < 9; i++) {
      const dark = i % 2 === 1;
      for (const line of rowLines(p.tiles, b.y + (i / 9) * b.h, 0.1)) {
        o.push(
          line.replace(
            '<line ',
            `<line stroke="${alpha(dark ? shade(fill, 0.3) : '#ffffff', dark ? 0.3 : 0.22)}" stroke-width="${dark ? 2.2 : 1.4}" `,
          ),
        );
      }
    }
  }
}

export function paintParcel(p: IsoParcel, P: Palette, o: string[]): void {
  const fill = parcelFill(p, P);
  if (!p.tiles.length) return;
  /* The ground itself is marked, the way the soil wash and the reach line are. It is the
     one path that exists for every parcel whatever is on it, so "did the farm paint at
     all" has something true to ask, and the tile names which parcel it is. */
  o.push(
    `<path data-testid="iso-parcel" data-tile="${p.tiles[0] ?? ''}" d="${unionPath(p.tiles)}" fill="${fill}" fill-rule="evenodd"/>`,
  );
  paintTexture(p, P, fill, o);
  /* D251: the pigs' pen is trodden to mud, with a puddle to every few tiles. Ground, not a
     standing thing, so it goes down here under every animal. */
  if (p.mud) {
    const b = unionBounds(p.tiles);
    const r = rnd(b.x * 13 + b.y * 5 + 1);
    for (const at of scatter(p.tiles, r, Math.max(1, Math.round(unionArea(p.tiles) / 4)), 0.35))
      paintPuddle(at[0], at[1], o);
  }
  /* D327: a monument ground's paths, and the low wall round a chapel yard. Ground, so down here. */
  if (p.ground) {
    const b = unionBounds(p.tiles);
    const g = monumentGround(b, p.ground.n, p.ground.marks.length);
    const line = (x1: number, y1: number, x2: number, y2: number, colour: string, w: number) => {
      const [ax, ay] = project(x1, y1),
        [bx, by] = project(x2, y2);
      o.push(
        `<path d="M${ax.toFixed(1)} ${ay.toFixed(1)} L${bx.toFixed(1)} ${by.toFixed(1)}" stroke="${colour}" stroke-width="${w}" stroke-linecap="round" fill="none"/>`,
      );
    };
    for (const [x1, y1, x2, y2] of g.paths) line(x1, y1, x2, y2, MEMORIAL.path, 5);
    if (g.wall) {
      const i = 0.12;
      const c: Array<[number, number]> = [
        [b.x + i, b.y + i],
        [b.x + b.w - i, b.y + i],
        [b.x + b.w - i, b.y + b.h - i],
        [b.x + i, b.y + b.h - i],
      ];
      c.forEach(([x, y], k) => {
        const [nx, ny] = c[(k + 1) % 4] as [number, number];
        line(x, y, nx, ny, MEMORIAL.wall, 3);
      });
    }
  }
  /* The outline, edge by edge rather than corner to corner, because the union may have
     more than four sides. Which way an edge faces is what decides whether it catches the
     light: the far two sides are the lit ones in this projection, the near two in shade. */
  for (const e of unionStrokes(p.tiles)) {
    const [ax, ay] = project(e.x1, e.y1),
      [bx, by] = project(e.x2, e.y2);
    const d = `M${ax.toFixed(1)} ${ay.toFixed(1)} L${bx.toFixed(1)} ${by.toFixed(1)}`;
    /* D178: a plan is lit evenly. There is nothing for the hour's light to fall across, so
       every side of the field takes the same line and the lit and shaded pair go. */
    if (isPlan()) {
      o.push(
        `<path d="${d}" fill="none" stroke="${alpha(shade(fill, 0.55), 0.4)}" stroke-width="1.6"/>`,
      );
      continue;
    }
    /* The facing it presents to the viewer, not the one it has in the world: the light
       stays fixed to the screen when the farm turns (D176). */
    const face = screenFacing(e.facing);
    if (face === 'n') {
      o.push(`<path d="${d}" stroke="${alpha(tint(fill, 0.85), P.lit * 1.4)}" stroke-width="2"/>`);
    } else if (face === 'w') {
      o.push(`<path d="${d}" stroke="${alpha(tint(fill, 0.7), P.lit)}" stroke-width="1.5"/>`);
    } else {
      o.push(
        `<path d="${d}" fill="none" stroke="${alpha(shade(fill, 0.8), P.dark * 0.9)}" stroke-width="1.6"/>`,
      );
    }
  }
}

/**
 * A run of hedge on a bank, as a box standing on the line it follows: a pale top that
 * catches the light and two shaded sides. Every hedge on the map is this shape; how tall
 * and how thick is all that separates the world's own hedgerow from the bank between two
 * fields (D161).
 */
function hedgeBox(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  ht: number,
  w: number,
  P: Palette,
  o: string[],
): void {
  const vert = Math.abs(x2 - x1) < 0.001;
  /* D178. A hedge from above is the one thing a plan has to draw differently rather than
     simply leave the height out of. Seen from the side it is read by its height: a pale
     top over two shaded sides, and the bank between two fields is told from the farm's own
     hedgerow by being lower. From above there is no height in either, and at their world
     widths the two are three pixels of the same green. So a plan gives each half again its
     width, which keeps the pair apart, and draws it in the hedge's own colour with a dark
     line round it rather than in the pale tint that only existed to catch the light. */
  if (isPlan()) {
    const pw = w * 1.5;
    const pts = rectPts(
      vert ? x1 - pw / 2 : x1,
      vert ? y1 : y1 - pw / 2,
      vert ? pw : x2 - x1,
      vert ? y2 - y1 : pw,
    );
    o.push(
      `<path d="${polyPath(pts)}" fill="${P.hedge}" stroke="${alpha(shade(P.hedge, 0.5), 0.75)}" stroke-width="0.8"/>`,
    );
    return;
  }
  isoBox(
    vert ? x1 - w / 2 : x1,
    vert ? y1 : y1 - w / 2,
    vert ? w : x2 - x1,
    vert ? y2 - y1 : w,
    ht,
    mix(P.hedge, HEDGE.topMix[0], HEDGE.topMix[1]),
    shade(P.hedge, HEDGE.leftShade),
    shade(P.hedge, HEDGE.rightShade),
    o,
  );
}

export function paintHedge(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  P: Palette,
  o: string[],
): void {
  hedgeBox(x1, y1, x2, y2, HEDGE.world.ht, HEDGE.world.w, P, o);
}

/**
 * D331: the world gate where the lane leaves the land, as the 3D world draws it (D287): two posts
 * either side of the lane and four bars across it, in the gate decoration's paint. `x`, `y` is
 * the gate's tile corner and `px`, `py` the lane's step before it. From above, the bars' line.
 */
export function paintFarmGate(
  g: { x: number; y: number; px: number; py: number },
  bars: string,
  post: string,
  o: string[],
): void {
  const f = (n: number) => n.toFixed(1);
  const dx = g.x - g.px,
    dy = g.y - g.py;
  const len = Math.hypot(dx, dy) || 1;
  // Across the lane: half its width and a little more.
  const half = 0.24;
  const ax = (-dy / len) * half,
    ay = (dx / len) * half;
  const A = (z: number) => project(g.x - ax, g.y - ay, z),
    B = (z: number) => project(g.x + ax, g.y + ay, z);
  if (isPlan()) {
    o.push(
      `<path d="M${f(A(0)[0])} ${f(A(0)[1])} L${f(B(0)[0])} ${f(B(0)[1])}" stroke="${bars}" stroke-width="2" stroke-linecap="round"/>`,
    );
    return;
  }
  for (const P of [A, B])
    o.push(
      `<path d="M${f(P(0)[0])} ${f(P(0)[1])} L${f(P(16)[0])} ${f(P(16)[1])}" stroke="${post}" stroke-width="2.2" stroke-linecap="round"/>`,
    );
  const lines = [4, 7, 10, 13].map(
    (z) => `M${f(A(z)[0])} ${f(A(z)[1])} L${f(B(z)[0])} ${f(B(z)[1])}`,
  );
  o.push(
    `<path d="${lines.join(' ')}" stroke="${bars}" stroke-width="1.3" stroke-linecap="round"/>`,
  );
}

/**
 * How tall and how thick the bank between two parcels is. Lower and thinner than the
 * world's own hedgerow, which is what keeps it reading as the line between two fields
 * rather than as a second boundary round each one, and it has to hold at both ends of the
 * zoom range: at 0.45 it is still about three pixels of light over three of shade, and it
 * takes a twentieth of a tile's width, so a field of three tiles is a field and not a
 * smudge.
 */
export const BANK_HT = HEDGE.bank.ht;
export const BANK_W = HEDGE.bank.w;

/**
 * The bank between two parcels (D161). Drawn with the land rather than over it, so the
 * hour's light and the region weather fall on it as they fall on everything else, and it
 * carries no state at all: every boundary gets the same bank, whichever field is selected.
 */
export function paintBoundary(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  P: Palette,
  o: string[],
): void {
  hedgeBox(x1, y1, x2, y2, BANK_HT, BANK_W, P, o);
}

/**
 * Every boundary between two parcels, painted back to front so a near bank crosses a far
 * one rather than the other way about.
 */
export function paintBoundaries(parcels: IsoParcel[], P: Palette, o: string[]): void {
  const runs = parcelBoundaries(parcels.map((p) => p.tiles));
  /* By the end of the run nearest the eye, so the order holds at every turn. At rest that
     is the run's own start, which is the order this has always drawn in. */
  const near = (e: { x1: number; y1: number; x2: number; y2: number }) =>
    Math.min(depth(e.x1, e.y1), depth(e.x2, e.y2));
  runs.sort((a, b) => near(a) - near(b));
  for (const e of runs) paintBoundary(e.x1, e.y1, e.x2, e.y2, P, o);
}

/** Post-and-rail along one edge. Every pasture has one; it is most of what says "pasture". */
export function paintFence(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  P: Palette,
  o: string[],
  style?: string,
): void {
  /* D291: a fence decoration other than post and rail draws its own fence. D331: in its
     shape's way and its own colour; a painted post and rail is post and rail in its colour. */
  const look = style ? FENCE_DECOR[style] : undefined;
  const shape = style ? FENCE_SHAPE[style] : undefined;
  if (look && shape && shape !== 'fence_rail') {
    paintFenceStyle(x1, y1, x2, y2, shape, look, o);
    return;
  }
  const FENCE = look ?? P.fence;
  const H = 11,
    dx = x2 - x1,
    dy = y2 - y1;
  const len = Math.hypot(dx, dy),
    n = Math.max(2, Math.round(len * 2.4));
  /* D178: from above a post-and-rail is the line it runs along and the posts on it. The
     two rails stand one above the other and are the same line from here, so one is drawn. */
  if (isPlan()) {
    const A = project(x1, y1),
      B = project(x2, y2);
    o.push(
      `<path d="M${A[0].toFixed(1)} ${A[1].toFixed(1)} L${B[0].toFixed(1)} ${B[1].toFixed(1)}" stroke="${shade(FENCE, 0.1)}" stroke-width="1.4" stroke-linecap="round"/>`,
    );
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const [px, py] = project(x1 + dx * t, y1 + dy * t);
      o.push(
        `<circle cx="${px.toFixed(1)}" cy="${py.toFixed(1)}" r="1.4" fill="${shade(FENCE, 0.34)}"/>`,
      );
    }
    return;
  }
  const rail = (k: number) => {
    const A = project(x1, y1, H * k),
      B = project(x2, y2, H * k);
    o.push(
      `<path d="M${A[0].toFixed(1)} ${A[1].toFixed(1)} L${B[0].toFixed(1)} ${B[1].toFixed(1)}" stroke="${shade(FENCE, 0.1)}" stroke-width="1.8" stroke-linecap="round"/>`,
    );
  };
  // D381: three rails, as the 3D pens have.
  rail(0.9);
  rail(0.6);
  rail(0.3);
  for (let i = 0; i <= n; i++) {
    const t = i / n,
      px = x1 + dx * t,
      py = y1 + dy * t;
    const base = project(px, py, 0),
      top = project(px, py, H);
    o.push(
      `<path d="M${base[0].toFixed(1)} ${base[1].toFixed(1)} L${top[0].toFixed(1)} ${top[1].toFixed(1)}" stroke="${shade(FENCE, 0.28)}" stroke-width="2.2" stroke-linecap="round"/>`,
    );
    o.push(
      `<path d="M${(base[0] - 0.6).toFixed(1)} ${base[1].toFixed(1)} L${(top[0] - 0.6).toFixed(1)} ${top[1].toFixed(1)}" stroke="${tint(FENCE, 0.22)}" stroke-width=".9" stroke-linecap="round"/>`,
    );
  }
}

/**
 * D291: a picket fence, a woven hurdle or a dry stone wall along one edge. Seen from above,
 * the line it runs along in its own colour.
 */
function paintFenceStyle(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  style: string,
  colour: string,
  o: string[],
): void {
  const f = (n: number) => n.toFixed(1);
  const A = project(x1, y1),
    B = project(x2, y2);
  if (isPlan()) {
    o.push(
      `<path d="M${f(A[0])} ${f(A[1])} L${f(B[0])} ${f(B[1])}" stroke="${colour}" stroke-width="2.2" stroke-linecap="round"/>`,
    );
    return;
  }
  const H = FENCE_HEIGHT[style] ?? 9;
  if (style === 'fence_picket') {
    const n = Math.max(3, Math.round(Math.hypot(x2 - x1, y2 - y1) * 7));
    const a = project(x1, y1, H * 0.6),
      b = project(x2, y2, H * 0.6);
    o.push(
      `<path d="M${f(a[0])} ${f(a[1])} L${f(b[0])} ${f(b[1])}" stroke="${shade(colour, 0.12)}" stroke-width="1.2"/>`,
    );
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const base = project(x1 + (x2 - x1) * t, y1 + (y2 - y1) * t, 0),
        top = project(x1 + (x2 - x1) * t, y1 + (y2 - y1) * t, H);
      o.push(
        `<path d="M${f(base[0])} ${f(base[1])} L${f(top[0])} ${f(top[1])}" stroke="${colour}" stroke-width="1.6" stroke-linecap="round"/>`,
      );
    }
    return;
  }
  const At = project(x1, y1, H),
    Bt = project(x2, y2, H);
  o.push(
    `<path d="M${f(A[0])} ${f(A[1])} L${f(B[0])} ${f(B[1])} L${f(Bt[0])} ${f(Bt[1])} L${f(At[0])} ${f(At[1])} Z" fill="${colour}" stroke="${shade(colour, 0.3)}" stroke-width="0.8"/>`,
  );
  /* Weave across a hurdle, courses along a wall. */
  const lines: string[] = [];
  for (const k of [0.3, 0.55, 0.8]) {
    const a = project(x1, y1, H * k),
      b = project(x2, y2, H * k);
    lines.push(`M${f(a[0])} ${f(a[1])} L${f(b[0])} ${f(b[1])}`);
  }
  o.push(
    `<path d="${lines.join(' ')}" stroke="${shade(colour, style === 'fence_hurdle' ? 0.3 : 0.22)}" stroke-width="0.8" fill="none"/>`,
  );
}

/* ---------- props ---------- */

export type TreeKind = 'wood' | 'orchard';
/** D199: `house` is the farmhouse's own shape, the one building a player must find. */
export type BuildingKind = 'barn' | 'shed' | 'silo' | 'house';

export function paintTree(
  wx: number,
  wy: number,
  size: number,
  P: Palette,
  kind: TreeKind,
  o: string[],
): void {
  const [cx, cy] = project(wx, wy);
  const leaf =
    kind === 'orchard' ? mix(P.orchard, TREE.orchardLeaf[0], TREE.orchardLeaf[1]) : P.wood;
  const lit = tint(leaf, P.lit * 1.1),
    dk = shade(leaf, P.dark);
  /* D178: a tree from above is its canopy, over its own point, and nothing else. No trunk,
     which is under the canopy, and no shadow, which a plan does not cast. Three overlapping
     crowns still, so it reads as foliage rather than as a dot. */
  if (isPlan()) {
    const r = size * 0.72;
    if (P.S.frost && kind !== 'orchard') {
      o.push(
        `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${r.toFixed(1)}" fill="${dk}"/>`,
      );
      o.push(
        `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${(r * 0.6).toFixed(1)}" fill="${alpha(TREE.frost, 0.55)}"/>`,
      );
    } else {
      o.push(
        `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${r.toFixed(1)}" fill="${dk}"/>`,
      );
      o.push(
        `<circle cx="${(cx - r * 0.12).toFixed(1)}" cy="${(cy - r * 0.12).toFixed(1)}" r="${(r * 0.82).toFixed(1)}" fill="${leaf}"/>`,
      );
      o.push(
        `<circle cx="${(cx - r * 0.28).toFixed(1)}" cy="${(cy - r * 0.28).toFixed(1)}" r="${(r * 0.42).toFixed(1)}" fill="${lit}"/>`,
      );
    }
    if (kind === 'orchard' && (P.S.blossom || P.S.stubble)) {
      const rr = rnd(wx * 71 + wy * 13),
        col = P.S.blossom ? TREE.blossom : TREE.fruit;
      for (let i = 0; i < 6; i++)
        o.push(
          `<circle cx="${(cx - r * 0.7 + rr() * r * 1.4).toFixed(1)}" cy="${(cy - r * 0.7 + rr() * r * 1.4).toFixed(1)}" r="1.3" fill="${col}"/>`,
        );
    }
    return;
  }
  const off = P.T.dx * size * 0.5 * P.T.len;
  o.push(
    `<ellipse cx="${(cx - off).toFixed(1)}" cy="${cy.toFixed(1)}" rx="${(size * 0.8).toFixed(1)}" ry="${(size * 0.32).toFixed(1)}" fill="${SHADOW}"/>`,
  );
  o.push(
    `<rect x="${(cx - size * 0.12).toFixed(1)}" y="${(cy - size * 1.05).toFixed(1)}" width="${(size * 0.24).toFixed(1)}" height="${(size * 1.05).toFixed(1)}" rx="1.5" fill="${shade(P.soil, TREE.trunkShade)}"/>`,
  );
  if (P.S.frost && kind !== 'orchard') {
    o.push(
      `<circle cx="${cx.toFixed(1)}" cy="${(cy - size * 1.45).toFixed(1)}" r="${(size * 0.64).toFixed(1)}" fill="${dk}"/>`,
    );
    o.push(
      `<circle cx="${(cx - size * 0.2).toFixed(1)}" cy="${(cy - size * 1.6).toFixed(1)}" r="${(size * 0.42).toFixed(1)}" fill="${alpha(TREE.frost, 0.55)}"/>`,
    );
  } else {
    /* Three overlapping crowns, darkest at the back, so the canopy has body. */
    o.push(
      `<circle cx="${(cx + size * 0.22).toFixed(1)}" cy="${(cy - size * 1.35).toFixed(1)}" r="${(size * 0.56).toFixed(1)}" fill="${dk}"/>`,
    );
    o.push(
      `<circle cx="${(cx - size * 0.18).toFixed(1)}" cy="${(cy - size * 1.42).toFixed(1)}" r="${(size * 0.62).toFixed(1)}" fill="${leaf}"/>`,
    );
    o.push(
      `<circle cx="${cx.toFixed(1)}" cy="${(cy - size * 1.72).toFixed(1)}" r="${(size * 0.5).toFixed(1)}" fill="${leaf}"/>`,
    );
    o.push(
      `<circle cx="${(cx - size * 0.26).toFixed(1)}" cy="${(cy - size * 1.78).toFixed(1)}" r="${(size * 0.3).toFixed(1)}" fill="${lit}"/>`,
    );
  }
  if (kind === 'orchard' && (P.S.blossom || P.S.stubble)) {
    const r = rnd(wx * 71 + wy * 13),
      col = P.S.blossom ? TREE.blossom : TREE.fruit;
    for (let i = 0; i < 6; i++)
      o.push(
        `<circle cx="${(cx - size * 0.55 + r() * size * 1.1).toFixed(1)}" cy="${(cy - size * 1.95 + r() * size * 0.8).toFixed(1)}" r="1.5" fill="${col}"/>`,
      );
  }
}

/** A cow, sheep or pig: body, head, legs, and the markings that name the animal. */
export function paintAnimal(
  wx: number,
  wy: number,
  kind: AnimalKind,
  P: Palette,
  o: string[],
  breed?: number,
): void {
  const [cx, cy] = project(wx, wy);
  const off = P.T.dx * 3 * P.T.len;
  const B = breed === undefined ? undefined : breedLook(kind, breed);
  if (kind === 'hen') {
    paintHen(cx, cy, off, o, B);
    return;
  }
  /* D284: the breed's look, or the plain animal's, from the shared appearance. */
  const look = breedLook(kind, breed);
  const hide = look.hide;
  const spots = look.spots;
  const head = look.head ?? hide;
  const leg = shade(hide, 0.45),
    L = 3.4;
  /* D178: from above an animal is its back and its head. The legs are under it, the
     markings are what name it, and there is no shadow. */
  if (isPlan()) {
    const headAt = project(wx + 0.16, wy);
    o.push(`<ellipse cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" rx="4.6" ry="3" fill="${hide}"/>`);
    if (spots) {
      o.push(
        `<ellipse cx="${(cx - 1.4).toFixed(1)}" cy="${(cy - 0.6).toFixed(1)}" rx="2" ry="1.5" fill="${spots}"/>`,
      );
      o.push(
        `<ellipse cx="${(cx + 1.6).toFixed(1)}" cy="${(cy + 1).toFixed(1)}" rx="1.2" ry="0.9" fill="${spots}"/>`,
      );
    }
    if (B?.belt)
      o.push(
        `<rect x="${(cx - 0.9).toFixed(1)}" y="${(cy - 3).toFixed(1)}" width="1.8" height="6" fill="${B.belt}"/>`,
      );
    o.push(
      `<circle cx="${headAt[0].toFixed(1)}" cy="${headAt[1].toFixed(1)}" r="1.9" fill="${kind === 'pig' ? shade(head, 0.22) : head}"/>`,
    );
    return;
  }
  o.push(
    `<ellipse cx="${(cx - off).toFixed(1)}" cy="${cy.toFixed(1)}" rx="6.4" ry="2.4" fill="rgba(42,36,24,.24)"/>`,
  );
  /* legs */
  (
    [
      [-3.4, -0.9],
      [-1.4, 0.7],
      [2, -0.9],
      [3.6, 0.6],
    ] as Array<[number, number]>
  ).forEach(([lx, ly]) =>
    o.push(
      `<rect x="${(cx + lx).toFixed(1)}" y="${(cy + ly - L).toFixed(1)}" width="1.5" height="${L}" fill="${leg}"/>`,
    ),
  );
  /* body */
  o.push(
    `<rect x="${(cx - 5).toFixed(1)}" y="${(cy - 8.6).toFixed(1)}" width="10" height="5.6" rx="2.6" fill="${hide}"/>`,
  );
  o.push(
    `<rect x="${(cx - 5).toFixed(1)}" y="${(cy - 5).toFixed(1)}" width="10" height="2" rx="1" fill="${alpha(shade(hide, 0.5), 0.35)}"/>`,
  );
  if (spots) {
    o.push(
      `<ellipse cx="${(cx - 1.6).toFixed(1)}" cy="${(cy - 7.2).toFixed(1)}" rx="2.4" ry="1.8" fill="${spots}"/>`,
    );
    o.push(
      `<ellipse cx="${(cx + 2.6).toFixed(1)}" cy="${(cy - 5.6).toFixed(1)}" rx="1.4" ry="1" fill="${spots}"/>`,
    );
  }
  /* D268: a belted breed, the Galloway or the Saddleback, wears its band round the middle. */
  if (B?.belt)
    o.push(
      `<rect x="${(cx - 1.2).toFixed(1)}" y="${(cy - 8.6).toFixed(1)}" width="2.6" height="5.6" fill="${B.belt}"/>`,
    );
  if (kind === 'cow') {
    o.push(
      `<circle cx="${(cx + 5.2).toFixed(1)}" cy="${(cy - 9.4).toFixed(1)}" r="2.2" fill="${head}"/>`,
    );
    o.push(
      `<path d="M${(cx + 4).toFixed(1)} ${(cy - 11).toFixed(1)} l-1 -1.6 M${(cx + 6.4).toFixed(1)} ${(cy - 11).toFixed(1)} l1 -1.6" stroke="${hide}" stroke-width="1.2" stroke-linecap="round"/>`,
    );
  } else if (kind === 'pig') {
    o.push(
      `<circle cx="${(cx + 5).toFixed(1)}" cy="${(cy - 7.4).toFixed(1)}" r="2.2" fill="${hide}"/>`,
    );
    o.push(
      `<circle cx="${(cx + 6.4).toFixed(1)}" cy="${(cy - 7).toFixed(1)}" r="1" fill="${shade(hide, 0.22)}"/>`,
    );
  } else {
    o.push(
      `<circle cx="${(cx - 4).toFixed(1)}" cy="${(cy - 8.2).toFixed(1)}" r="2.2" fill="${tint(hide, 0.3)}"/>`,
    );
    o.push(
      `<circle cx="${(cx + 5).toFixed(1)}" cy="${(cy - 8.4).toFixed(1)}" r="2" fill="${head}"/>`,
    );
  }
  o.push(
    `<path d="M${(cx - 5.2).toFixed(1)} ${(cy - 8).toFixed(1)} l-1.6 2.4" stroke="${shade(hide, 0.4)}" stroke-width="1.1" stroke-linecap="round"/>`,
  );
}

/** A hen (D240): a small round body, a tail, a head with a red comb. Seen from above, a dot. */
function paintHen(cx: number, cy: number, off: number, o: string[], B?: Breed): void {
  const body = B?.hide ?? PLAIN_ANIMAL.hen.hide;
  if (isPlan()) {
    o.push(`<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="2.2" fill="${body}"/>`);
    o.push(
      `<circle cx="${(cx + 1.6).toFixed(1)}" cy="${(cy - 0.4).toFixed(1)}" r="0.9" fill="${HEN.comb}"/>`,
    );
    return;
  }
  o.push(
    `<ellipse cx="${(cx - off * 0.5).toFixed(1)}" cy="${cy.toFixed(1)}" rx="3.2" ry="1.3" fill="${SHADOW}"/>`,
  );
  o.push(
    `<path d="M${(cx - 0.6).toFixed(1)} ${cy.toFixed(1)} v-1.6 M${(cx + 0.8).toFixed(1)} ${cy.toFixed(1)} v-1.6" stroke="${HEN.beak}" stroke-width="0.7"/>`,
  );
  o.push(
    `<path d="M${(cx - 3.4).toFixed(1)} ${(cy - 5.6).toFixed(1)} l1.6 2.2 l-1.2 0.6 z" fill="${shade(body, 0.12)}"/>`,
  );
  o.push(
    `<ellipse cx="${cx.toFixed(1)}" cy="${(cy - 3.4).toFixed(1)}" rx="3" ry="2.2" fill="${body}"/>`,
  );
  if (B?.speck)
    o.push(
      `<g fill="${B.speck}"><circle cx="${(cx - 1.2).toFixed(1)}" cy="${(cy - 3.8).toFixed(1)}" r="0.35"/><circle cx="${(cx + 0.4).toFixed(1)}" cy="${(cy - 2.6).toFixed(1)}" r="0.35"/><circle cx="${(cx + 1.2).toFixed(1)}" cy="${(cy - 4.2).toFixed(1)}" r="0.35"/><circle cx="${(cx - 0.4).toFixed(1)}" cy="${(cy - 4.8).toFixed(1)}" r="0.3"/></g>`,
    );
  o.push(
    `<circle cx="${(cx + 2.4).toFixed(1)}" cy="${(cy - 5.6).toFixed(1)}" r="1.4" fill="${body}"/>`,
  );
  o.push(
    `<path d="M${(cx + 2).toFixed(1)} ${(cy - 7).toFixed(1)} q0.5 -1 1 0" fill="${HEN.comb}"/>`,
  );
  o.push(
    `<path d="M${(cx + 3.7).toFixed(1)} ${(cy - 5.7).toFixed(1)} l1 0.3 l-1 0.3 z" fill="${HEN.beak}"/>`,
  );
}

/**
 * D259: the height a standing thing reaches above its ground, in screen units at rest, so
 * a still map can frame the whole of it. Upper bounds, read off the painters below.
 */
export function standingHeight(kind: BuildingKind | 'mark', w = 1): number {
  if (kind === 'silo') return 52 * (w > 0.7 ? 1 : 0.8) + 46 * 0.3 * w * 0.95 + 4;
  if (kind === 'barn') return 30 + 21 + 8;
  if (kind === 'house') return HOUSE_WALL + HOUSE_RISE + 18;
  if (kind === 'mark') return 30 * MARK_SCALE;
  return 22 + 18;
}

/** Paint into `o` scaled by `k` about the screen point (cx, cy). */
function scaleAbout(
  cx: number,
  cy: number,
  k: number,
  o: string[],
  paint: (q: string[]) => void,
): void {
  const q: string[] = [];
  paint(q);
  o.push(
    `<g transform="translate(${(cx * (1 - k)).toFixed(1)} ${(cy * (1 - k)).toFixed(1)}) scale(${k})">${q.join('')}</g>`,
  );
}

/**
 * D245: the lasting marks the harvest festivals leave on the world. Each is a few flat shapes
 * in the map's own hand, standing on its point. From above, each is its footprint.
 */
function paintMark(kind: string, wx: number, wy: number, o: string[]): void {
  const [cx, cy] = project(wx, wy);
  const f = (n: number) => n.toFixed(1);
  const stone = MARK.stone,
    dark = MARK.stoneDark,
    wood = MARK.wood;
  if (isPlan()) {
    o.push(
      `<circle cx="${f(cx)}" cy="${f(cy)}" r="4" fill="${stone}" stroke="${dark}" stroke-width="0.8"/>`,
    );
    return;
  }
  o.push(`<ellipse cx="${f(cx)}" cy="${f(cy)}" rx="8" ry="3" fill="${SHADOW}"/>`);
  switch (kind) {
    case 'standing_stone':
      o.push(
        `<path d="M${f(cx - 4)} ${f(cy)} L${f(cx - 3)} ${f(cy - 20)} Q${f(cx)} ${f(cy - 24)} ${f(cx + 3)} ${f(cy - 20)} L${f(cx + 4)} ${f(cy)} z" fill="${stone}"/>`,
      );
      o.push(
        `<path d="M${f(cx + 1)} ${f(cy - 21)} L${f(cx + 4)} ${f(cy)} L${f(cx + 1)} ${f(cy)} z" fill="${dark}"/>`,
      );
      return;
    case 'hedgerow':
      for (const dx of [-7, 0, 7])
        o.push(
          `<ellipse cx="${f(cx + dx)}" cy="${f(cy - 5)}" rx="5" ry="5.5" fill="${MARK.leaf}"/>`,
        );
      o.push(
        `<circle cx="${f(cx - 3)}" cy="${f(cy - 7)}" r="1" fill="${MARK.blossom}"/><circle cx="${f(cx + 5)}" cy="${f(cy - 4)}" r="1" fill="${MARK.blossom}"/>`,
      );
      return;
    case 'windmill':
      o.push(
        `<path d="M${f(cx - 5)} ${f(cy)} L${f(cx - 3)} ${f(cy - 22)} L${f(cx + 3)} ${f(cy - 22)} L${f(cx + 5)} ${f(cy)} z" fill="${MARK.white}"/>`,
      );
      o.push(
        `<path d="M${f(cx - 4)} ${f(cy - 22)} L${f(cx)} ${f(cy - 27)} L${f(cx + 4)} ${f(cy - 22)} z" fill="${wood}"/>`,
      );
      for (const a of [20, 110, 200, 290]) {
        const r = (a * Math.PI) / 180;
        o.push(
          `<path d="M${f(cx)} ${f(cy - 21)} l${f(Math.cos(r) * 13)} ${f(Math.sin(r) * 13)}" stroke="${wood}" stroke-width="2.2" stroke-linecap="round"/>`,
        );
      }
      return;
    case 'dovecote':
      o.push(
        `<rect x="${f(cx - 4)}" y="${f(cy - 14)}" width="8" height="14" fill="${MARK.dovecote}"/>`,
      );
      o.push(
        `<path d="M${f(cx - 6)} ${f(cy - 14)} L${f(cx)} ${f(cy - 22)} L${f(cx + 6)} ${f(cy - 14)} z" fill="${MARK.tile}"/>`,
      );
      o.push(`<circle cx="${f(cx)}" cy="${f(cy - 9)}" r="1.4" fill="${MARK.hole}"/>`);
      return;
    case 'well':
      o.push(
        `<ellipse cx="${f(cx)}" cy="${f(cy - 3)}" rx="6" ry="3" fill="${stone}" stroke="${dark}" stroke-width="0.8"/>`,
      );
      o.push(
        `<path d="M${f(cx - 5)} ${f(cy - 3)} v-10 M${f(cx + 5)} ${f(cy - 3)} v-10" stroke="${wood}" stroke-width="1.4"/>`,
      );
      o.push(
        `<path d="M${f(cx - 7)} ${f(cy - 12)} L${f(cx)} ${f(cy - 18)} L${f(cx + 7)} ${f(cy - 12)} z" fill="${MARK.tile}"/>`,
      );
      return;
    case 'maypole':
      o.push(`<path d="M${f(cx)} ${f(cy)} v-26" stroke="${MARK.white}" stroke-width="1.6"/>`);
      for (const [dx, c] of [
        [-7, MARK.ribbons[0]],
        [0, MARK.ribbons[1]],
        [7, MARK.ribbons[2]],
      ] as Array<[number, string]>)
        o.push(
          `<path d="M${f(cx)} ${f(cy - 25)} Q${f(cx + dx * 0.6)} ${f(cy - 14)} ${f(cx + dx)} ${f(cy - 2)}" stroke="${c}" stroke-width="1" fill="none"/>`,
        );
      o.push(`<circle cx="${f(cx)}" cy="${f(cy - 26)}" r="2.4" fill="${MARK.leaf}"/>`);
      return;
    case 'stone_bridge':
      o.push(
        `<path d="M${f(cx - 10)} ${f(cy)} L${f(cx - 10)} ${f(cy - 7)} L${f(cx + 10)} ${f(cy - 7)} L${f(cx + 10)} ${f(cy)} L${f(cx + 5)} ${f(cy)} Q${f(cx)} ${f(cy - 6)} ${f(cx - 5)} ${f(cy)} z" fill="${stone}"/>`,
      );
      o.push(`<path d="M${f(cx - 10)} ${f(cy - 7)} h20" stroke="${dark}" stroke-width="1.2"/>`);
      return;
    case 'beacon':
      o.push(`<path d="M${f(cx)} ${f(cy)} v-20" stroke="${wood}" stroke-width="2"/>`);
      o.push(`<path d="M${f(cx - 4)} ${f(cy - 20)} h8 l-1.5 4 h-5 z" fill="${MARK.hole}"/>`);
      o.push(
        `<path d="M${f(cx - 3)} ${f(cy - 20)} Q${f(cx)} ${f(cy - 30)} ${f(cx + 3)} ${f(cy - 20)} z" fill="${MARK.flame}"/>`,
      );
      return;
    default:
      // The monument: an obelisk on a plinth.
      o.push(`<rect x="${f(cx - 6)}" y="${f(cy - 4)}" width="12" height="4" fill="${dark}"/>`);
      o.push(
        `<path d="M${f(cx - 3)} ${f(cy - 4)} L${f(cx - 2)} ${f(cy - 24)} L${f(cx)} ${f(cy - 27)} L${f(cx + 2)} ${f(cy - 24)} L${f(cx + 3)} ${f(cy - 4)} z" fill="${stone}"/>`,
      );
      o.push(
        `<path d="M${f(cx)} ${f(cy - 27)} L${f(cx + 2)} ${f(cy - 24)} L${f(cx + 3)} ${f(cy - 4)} L${f(cx)} ${f(cy - 4)} z" fill="${dark}"/>`,
      );
  }
}

/**
 * D327: one memorial on a monument ground, in its material and at its size: a standing stone,
 * a cross, a brick pillar, a flint cairn, a timber post, a shrub, or the chapel itself.
 */
function paintMemorial(m: Extract<Prop, { k: 'memorial' }>, P: Palette, o: string[]): void {
  const [cx, cy] = project(m.x, m.y);
  const f = (n: number) => n.toFixed(1);
  const mat = MEMORIAL.materials[m.material] ?? MEMORIAL.materials.stone!;
  const z = m.size * 1.3 * (isPlan() ? 0 : 1);
  const s = m.size * 1.3;
  switch (m.form) {
    case 'chapel': {
      const w = m.w ?? 1,
        h = m.h ?? 1;
      const pts = [
        project(m.x - w / 2, m.y - h / 2),
        project(m.x + w / 2, m.y - h / 2),
        project(m.x + w / 2, m.y + h / 2),
        project(m.x - w / 2, m.y + h / 2),
      ];
      const lift = isPlan() ? 0 : 16;
      const poly = (q: Array<[number, number]>, dy: number) =>
        q.map(([x, y]) => `${f(x)},${f(y - dy)}`).join(' ');
      // Walls: the two near faces, then a roof over the footprint lifted, and a bell-cote.
      const [a, b, c, d] = pts as Array<[number, number]>;
      o.push(
        `<polygon points="${poly([b!, c!], 0)} ${poly([c!, b!], lift)}" fill="${shade(MEMORIAL.chapel.wall, 0.12)}"/>`,
      );
      o.push(
        `<polygon points="${poly([c!, d!], 0)} ${poly([d!, c!], lift)}" fill="${MEMORIAL.chapel.wall}"/>`,
      );
      o.push(`<polygon points="${poly([a!, b!, c!, d!], lift)}" fill="${MEMORIAL.chapel.roof}"/>`);
      const [tx, ty] = project(m.x - w / 2 + 0.2, m.y);
      o.push(
        `<rect x="${f(tx - 3)}" y="${f(ty - lift - 12)}" width="6" height="12" fill="${MEMORIAL.chapel.wall}"/>`,
      );
      o.push(
        `<path d="M${f(tx - 4)} ${f(ty - lift - 12)} L${f(tx)} ${f(ty - lift - 18)} L${f(tx + 4)} ${f(ty - lift - 12)} z" fill="${MEMORIAL.chapel.roof}"/>`,
      );
      return;
    }
    case 'shrub':
      o.push(
        `<ellipse cx="${f(cx)}" cy="${f(cy - 4 * z)}" rx="6" ry="5" fill="${MEMORIAL.shrub}"/>`,
      );
      o.push(
        `<ellipse cx="${f(cx - 1.5)}" cy="${f(cy - 5.5 * z)}" rx="3" ry="2.4" fill="${tint(MEMORIAL.shrub, P.lit)}"/>`,
      );
      return;
    case 'cairn':
      o.push(
        `<ellipse cx="${f(cx)}" cy="${f(cy - 2 * z)}" rx="${f(6 * s)}" ry="${f(3.5 * s)}" fill="${mat.dark}"/>`,
      );
      o.push(
        `<ellipse cx="${f(cx)}" cy="${f(cy - 5 * z)}" rx="${f(4 * s)}" ry="${f(3 * s)}" fill="${mat.face}"/>`,
      );
      o.push(
        `<ellipse cx="${f(cx)}" cy="${f(cy - 8 * z)}" rx="${f(2.2 * s)}" ry="${f(2 * s)}" fill="${mat.face}"/>`,
      );
      return;
    case 'cross':
      o.push(
        `<rect x="${f(cx - 1.5 * s)}" y="${f(cy - 16 * z)}" width="${f(3 * s)}" height="${f(16 * z)}" fill="${mat.face}"/>`,
      );
      o.push(
        `<rect x="${f(cx - 5 * s)}" y="${f(cy - 12 * z)}" width="${f(10 * s)}" height="${f(2.6 * s)}" fill="${mat.face}"/>`,
      );
      o.push(
        `<rect x="${f(cx)}" y="${f(cy - 16 * z)}" width="${f(1.5 * s)}" height="${f(16 * z)}" fill="${mat.dark}"/>`,
      );
      return;
    case 'pillar':
      o.push(
        `<rect x="${f(cx - 3.5 * s)}" y="${f(cy - 14 * z)}" width="${f(7 * s)}" height="${f(14 * z)}" fill="${mat.face}"/>`,
      );
      o.push(
        `<rect x="${f(cx)}" y="${f(cy - 14 * z)}" width="${f(3.5 * s)}" height="${f(14 * z)}" fill="${mat.dark}"/>`,
      );
      o.push(
        `<rect x="${f(cx - 4.5 * s)}" y="${f(cy - 16 * z)}" width="${f(9 * s)}" height="${f(2.4 * s)}" fill="${MEMORIAL.materials.stone!.face}"/>`,
      );
      return;
    case 'post':
      o.push(
        `<rect x="${f(cx - 2 * s)}" y="${f(cy - 13 * z)}" width="${f(4 * s)}" height="${f(13 * z)}" fill="${mat.face}"/>`,
      );
      o.push(
        `<path d="M${f(cx - 2.6 * s)} ${f(cy - 13 * z)} L${f(cx)} ${f(cy - 16 * z)} L${f(cx + 2.6 * s)} ${f(cy - 13 * z)} z" fill="${mat.dark}"/>`,
      );
      return;
    default:
      // A standing stone, rough-topped.
      o.push(
        `<path d="M${f(cx - 4 * s)} ${f(cy)} L${f(cx - 3 * s)} ${f(cy - 15 * z)} L${f(cx + 1 * s)} ${f(cy - 17 * z)} L${f(cx + 4 * s)} ${f(cy - 13 * z)} L${f(cx + 4 * s)} ${f(cy)} z" fill="${mat.face}"/>`,
      );
      o.push(
        `<path d="M${f(cx + 1 * s)} ${f(cy - 17 * z)} L${f(cx + 4 * s)} ${f(cy - 13 * z)} L${f(cx + 4 * s)} ${f(cy)} L${f(cx + 1 * s)} ${f(cy)} z" fill="${mat.dark}"/>`,
      );
  }
}

/** A beehive (D240): a white box of two supers under a pitched lid. From above, the lid. */
function paintHive(wx: number, wy: number, o: string[]): void {
  const [cx, cy] = project(wx, wy);
  if (isPlan()) {
    o.push(
      `<rect x="${(cx - 3).toFixed(1)}" y="${(cy - 3).toFixed(1)}" width="6" height="6" fill="${HIVE.planLid}" stroke="${HIVE.planEdge}" stroke-width="0.6"/>`,
    );
    return;
  }
  o.push(
    `<ellipse cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" rx="4.6" ry="1.6" fill="${SHADOW}"/>`,
  );
  o.push(
    `<rect x="${(cx - 3.6).toFixed(1)}" y="${(cy - 7.4).toFixed(1)}" width="7.2" height="7" fill="${HIVE.body}"/>`,
  );
  o.push(
    `<rect x="${(cx - 3.6).toFixed(1)}" y="${(cy - 4).toFixed(1)}" width="7.2" height="0.8" fill="${HIVE.band}"/>`,
  );
  o.push(
    `<rect x="${(cx - 1).toFixed(1)}" y="${(cy - 1.4).toFixed(1)}" width="2" height="0.9" fill="${HIVE.door}"/>`,
  );
  o.push(
    `<path d="M${(cx - 4.4).toFixed(1)} ${(cy - 7.4).toFixed(1)} L${cx.toFixed(1)} ${(cy - 9.6).toFixed(1)} L${(cx + 4.4).toFixed(1)} ${(cy - 7.4).toFixed(1)} z" fill="${HIVE.lid}"/>`,
  );
}

/**
 * D300: a lean-to glasshouse over tomatoes: a timber frame, two glass panes and a row of red
 * fruit showing through. Grows a little taller with every milestone (D268).
 */
function paintGlasshouse(wx: number, wy: number, tier: number, o: string[]): void {
  const [cx, cy] = project(wx, wy);
  const wall = GLASSHOUSE.wall + tier * GLASSHOUSE.growPerTier;
  if (isPlan()) {
    o.push(
      `<rect x="${(cx - 3.6).toFixed(1)}" y="${(cy - 4).toFixed(1)}" width="7.2" height="8" fill="${alpha(GLASSHOUSE.glass, GLASSHOUSE.glassAlpha)}" stroke="${GLASSHOUSE.frame}" stroke-width="0.7"/>`,
    );
    o.push(
      `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="1" fill="${GLASSHOUSE.tomato}"/>`,
    );
    return;
  }
  o.push(
    `<ellipse cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" rx="4.4" ry="1.7" fill="${SHADOW}"/>`,
  );
  const top = cy - wall;
  o.push(
    `<path d="M${(cx - 4).toFixed(1)} ${cy.toFixed(1)} L${(cx - 4).toFixed(1)} ${top.toFixed(1)} L${cx.toFixed(1)} ${(top - 3).toFixed(1)} L${(cx + 4).toFixed(1)} ${top.toFixed(1)} L${(cx + 4).toFixed(1)} ${cy.toFixed(1)} z" fill="${alpha(GLASSHOUSE.glass, GLASSHOUSE.glassAlpha)}" stroke="${GLASSHOUSE.frame}" stroke-width="0.8"/>`,
  );
  o.push(
    `<path d="M${cx.toFixed(1)} ${(top - 3).toFixed(1)} L${cx.toFixed(1)} ${cy.toFixed(1)} M${(cx - 4).toFixed(1)} ${(top + wall * 0.4).toFixed(1)} L${(cx + 4).toFixed(1)} ${(top + wall * 0.4).toFixed(1)}" stroke="${GLASSHOUSE.frame}" stroke-width="0.6"/>`,
  );
  for (const fx of [-2.2, 0.2, 2.2])
    o.push(
      `<circle cx="${(cx + fx).toFixed(1)}" cy="${(cy - 1.4).toFixed(1)}" r="0.8" fill="${GLASSHOUSE.tomato}"/>`,
    );
}

/** D301: a trout just under the surface: a dark back, a pale flank, no shadow (it is wet). */
function paintFish(wx: number, wy: number, o: string[]): void {
  const [cx, cy] = project(wx, wy);
  const ry = isPlan() ? 2.2 : 1.1;
  o.push(
    `<ellipse cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" rx="3.4" ry="${ry}" fill="${FISH.body}"/>`,
  );
  o.push(
    `<ellipse cx="${(cx - 0.6).toFixed(1)}" cy="${(cy + ry * 0.3).toFixed(1)}" rx="2.2" ry="${(ry * 0.5).toFixed(1)}" fill="${FISH.belly}"/>`,
  );
  o.push(
    `<path d="M${(cx + 3.2).toFixed(1)} ${cy.toFixed(1)} l1.6 -1 l0 2 z" fill="${FISH.body}"/>`,
  );
}

/**
 * D345: a crop waiting to be tapped, no hand and its bar full: the crop's own icon on a
 * small gold disc, bobbing over the field (`.map-ready-bob`, frozen under reduced motion).
 * Not drawn from above, where nothing else keeps its height either.
 */
function paintReady(wx: number, wy: number, id: string, icon: string, o: string[]): void {
  if (isPlan()) return;
  const [cx, cy] = project(wx, wy);
  const ly = cy - READY_LIFT;
  // A soft shadow on the ground, right under it, same as anything else standing (D318's own).
  o.push(`<ellipse cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" rx="12" ry="4" fill="${SHADOW}"/>`);
  // About 1.5 tiles across: a coloured rim, a white face, the crop's own icon on it, so it
  // reads at a glance rather than as a dot (D345).
  o.push(
    `<g class="map-ready-bob" data-testid="map-ready-${id}">` +
      `<circle cx="${cx.toFixed(1)}" cy="${ly.toFixed(1)}" r="17" fill="${READY_BADGE.rim}"/>` +
      `<circle cx="${cx.toFixed(1)}" cy="${ly.toFixed(1)}" r="14" fill="${READY_BADGE.face}"/>` +
      `<svg x="${(cx - 11).toFixed(1)}" y="${(ly - 11).toFixed(1)}" width="22" height="22" color="${READY_BADGE.ink}"><use href="#icon-${icon}"/></svg>` +
      `</g>`,
  );
}

/** A vine on its stake (D240): a stake, a band of leaves and a bunch of grapes. */
function paintVine(wx: number, wy: number, o: string[]): void {
  const [cx, cy] = project(wx, wy);
  if (isPlan()) {
    o.push(`<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="2.2" fill="${VINE.leaf}"/>`);
    return;
  }
  o.push(
    `<path d="M${cx.toFixed(1)} ${cy.toFixed(1)} v-8" stroke="${VINE.stake}" stroke-width="0.8"/>`,
  );
  o.push(
    `<ellipse cx="${cx.toFixed(1)}" cy="${(cy - 6.4).toFixed(1)}" rx="3.2" ry="2.4" fill="${VINE.leaf}"/>`,
  );
  o.push(
    `<circle cx="${(cx + 0.8).toFixed(1)}" cy="${(cy - 4.4).toFixed(1)}" r="1" fill="${VINE.grapes}"/>`,
  );
}

/**
 * D251: a hop pole with its bine. A tall pole, a wire off its top, and the bine wound up it
 * in leaf with pale green cones hanging. From above, the leaf round the pole's foot.
 */
function paintHop(wx: number, wy: number, o: string[]): void {
  const [cx, cy] = project(wx, wy);
  const f = (n: number) => n.toFixed(1);
  if (isPlan()) {
    o.push(`<circle cx="${f(cx)}" cy="${f(cy)}" r="3" fill="${VINE.leaf}"/>`);
    o.push(`<circle cx="${f(cx)}" cy="${f(cy)}" r="1" fill="${HOP.pole}"/>`);
    return;
  }
  o.push(`<ellipse cx="${f(cx)}" cy="${f(cy)}" rx="3.4" ry="1.3" fill="${SHADOW}"/>`);
  o.push(`<path d="M${f(cx)} ${f(cy)} v-28" stroke="${HOP.pole}" stroke-width="1.4"/>`);
  o.push(
    `<path d="M${f(cx)} ${f(cy - 28)} l9 -2" stroke="${HOP.wire}" stroke-width="0.6" fill="none"/>`,
  );
  o.push(
    `<path d="M${f(cx)} ${f(cy - 2)} q-4 -4 0 -8 q4 -4 0 -8 q-4 -4 0 -8" stroke="${HOP.bine}" stroke-width="2.6" fill="none" stroke-linecap="round"/>`,
  );
  for (const [dx, dy] of [
    [-2.6, -8],
    [2.4, -13],
    [-2.2, -19],
    [2.2, -24],
  ] as Array<[number, number]>)
    o.push(`<ellipse cx="${f(cx + dx)}" cy="${f(cy + dy)}" rx="1.3" ry="1.9" fill="${HOP.cone}"/>`);
}

/**
 * D251: a clump of saffron crocus, low in its bed: three purple cups with an orange
 * stigma in the middle one. From above, the cups as a ring of purple dots.
 */
function paintCrocus(wx: number, wy: number, o: string[]): void {
  const [cx, cy] = project(wx, wy);
  const f = (n: number) => n.toFixed(1);
  const cups: Array<[number, number]> = [
    [-2.6, 0.4],
    [0, -0.8],
    [2.6, 0.4],
  ];
  for (const [dx, dy] of cups) {
    if (!isPlan())
      o.push(
        `<path d="M${f(cx + dx)} ${f(cy + dy)} v-2.4" stroke="${CROCUS.stem}" stroke-width="0.7"/>`,
      );
    const lift = isPlan() ? 0 : 3.4;
    o.push(
      `<path d="M${f(cx + dx - 1.4)} ${f(cy + dy - lift)} q1.4 -2.6 2.8 0 q-1.4 1.4 -2.8 0z" fill="${CROCUS.cup}"/>`,
    );
  }
  o.push(
    `<circle cx="${f(cx)}" cy="${f(cy - (isPlan() ? 0.8 : 4.6))}" r="0.6" fill="${CROCUS.stigma}"/>`,
  );
}

/** D251: a puddle in the pigs' pen, flat on the ground. */
function paintPuddle(wx: number, wy: number, o: string[]): void {
  const [cx, cy] = project(wx, wy);
  o.push(
    `<ellipse cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" rx="7" ry="3" fill="${PUDDLE}" opacity=".75"/>`,
  );
  o.push(
    `<ellipse cx="${(cx - 1.5).toFixed(1)}" cy="${(cy - 0.6).toFixed(1)}" rx="2.6" ry="0.9" fill="#ffffff" opacity=".18"/>`,
  );
}

/** How far the drop shadow falls, in screen pixels. Down the screen, never along an axis. */
export const SHADOW_DROP = TH * 0.12;

/**
 * A barn, shed or silo. Barns take the gambrel roof and red board the reference worlds
 * use; sheds are a simple pitch on the pale wall; silos are a cylinder with a domed cap.
 * A building is genuinely a rectangle, unlike a field, so it keeps x, y, w, h.
 *
 * D176 is the one painter a coordinate transform does not carry on its own. Two wide and
 * three deep is three wide and two deep from the side, which the projection does give for
 * nothing, but the faces are drawn rather than derived: a gambrel shows one of its two
 * slopes and one of its two gable ends, a shed's pitches swap which of them the light
 * falls on, and the walls the box paints are the two nearest the eye. Each of those is
 * chosen here from the turn, and the shadow falls down the screen rather than along a
 * world axis, because the light is fixed to the screen and does not turn with the world.
 */
export function paintBuilding(
  x: number,
  y: number,
  w: number,
  h: number,
  P: Palette,
  kind: BuildingKind,
  o: string[],
  use?: BuildingUse,
  style?: BuildingStyle,
  look?: string,
  chain?: string,
): void {
  /* D291: a building decoration repaints the walls or the roof of every building. */
  /* D331: and a roof decoration the roof. */
  const decor = buildingDecor(look);
  const Pd: Palette =
    decor.wall || decor.barn ? { ...P, wall: decor.wall ?? P.wall, barn: decor.barn ?? P.barn } : P;
  /* D196: the roof takes the building's use; one with no known use keeps the slate. */
  /* D343: a chain building's own kind wins over the shared category. */
  const BARN = Pd.barn,
    SLATE = decor.roof ?? (chain && CHAIN_ROOF[chain]) ?? (use ? P.roofs[use] : P.slate),
    TRIM = P.trim;
  paintBuildingBody(
    x,
    y,
    w,
    h,
    Pd,
    kind,
    o,
    BARN,
    SLATE,
    TRIM,
    decor.wall ? undefined : style,
    style,
    chain,
  );
  if (use) paintSign(x, y, w, h, P, kind, use, o);
}

function paintBuildingBody(
  x: number,
  y: number,
  w: number,
  h: number,
  P: Palette,
  kind: BuildingKind,
  o: string[],
  BARN: string,
  SLATE: string,
  TRIM: string,
  style?: BuildingStyle,
  /** The building's tier, for its height and grain when a decoration has repainted it. */
  tierStyle: BuildingStyle | undefined = style,
  /** D346: the chain's own id, for its own silhouette (`paintChainMark`). */
  chain?: string,
): void {
  /* D178: no shadow from above. A plan is lit evenly, so nothing has a side the light can
     miss and nothing has anywhere to cast. */
  if (!isPlan())
    o.push(
      `<path d="${polyPath(
        rectPts(x, y, w, h).map((q) => [q[0], q[1] + SHADOW_DROP] as Point),
      )}" fill="rgba(42,36,24,.20)"/>`,
    );
  if (kind === 'house') {
    paintHouse(x, y, w, h, P, o, SLATE, TRIM);
    return;
  }
  /* D178. A building from above is its roof. A silo is a circle, because that is what a
     cylinder is from here; a barn and a shed are their own rectangle with the ridge
     running the way the building does, long way along. None of it is the isometric
     drawing squashed: the walls, the gable and the door are under the roof and simply are
     not there to draw. */
  if (isPlan()) {
    if (kind === 'silo') {
      const [sx, sy] = project(x + w / 2, y + h / 2);
      const r = PLAN_TILE * 0.38 * w;
      o.push(
        `<circle cx="${sx.toFixed(1)}" cy="${sy.toFixed(1)}" r="${r.toFixed(1)}" fill="${SLATE}"/>`,
      );
      o.push(
        `<circle cx="${sx.toFixed(1)}" cy="${sy.toFixed(1)}" r="${(r * 0.62).toFixed(1)}" fill="none" stroke="${tint(SLATE, 0.3)}" stroke-width="1.4"/>`,
      );
      o.push(
        `<circle cx="${sx.toFixed(1)}" cy="${sy.toFixed(1)}" r="${(r * 0.16).toFixed(1)}" fill="${tint(SLATE, 0.3)}"/>`,
      );
      return;
    }
    const ov = 0.06;
    o.push(
      `<path d="${polyPath(rectPts(x - ov, y - ov, w + ov * 2, h + ov * 2))}" fill="${SLATE}" stroke="${shade(SLATE, 0.32)}" stroke-width="1"/>`,
    );
    /* The ridge runs the long way of the building, and the barn's gambrel shows its two
       knees either side of it, which is what tells a barn from a shed with no height in
       either of them. */
    const along = w >= h;
    const line = (f: number, stroke: string, width: number) => {
      const a = along ? project(x, y + h * f) : project(x + w * f, y);
      const b = along ? project(x + w, y + h * f) : project(x + w * f, y + h);
      o.push(
        `<path d="M${a[0].toFixed(1)} ${a[1].toFixed(1)} L${b[0].toFixed(1)} ${b[1].toFixed(1)}" stroke="${stroke}" stroke-width="${width}" stroke-linecap="round"/>`,
      );
    };
    if (kind === 'barn') {
      line(0.26, alpha(shade(SLATE, 0.3), 0.7), 1);
      line(0.74, alpha(shade(SLATE, 0.3), 0.7), 1);
    }
    line(0.5, tint(SLATE, 0.34), 1.8);
    /* The one thing a roof does carry of the building under it: a barn's own board colour
       at the gable ends, so the yard still reads as a barn and a shed beside it. */
    if (kind === 'barn') {
      const ends = along
        ? [
            [x - ov, y - ov, ov * 2, h + ov * 2],
            [x + w - ov, y - ov, ov * 2, h + ov * 2],
          ]
        : [
            [x - ov, y - ov, w + ov * 2, ov * 2],
            [x - ov, y + h - ov, w + ov * 2, ov * 2],
          ];
      for (const [ex, ey, ew, eh] of ends as Array<[number, number, number, number]>)
        o.push(`<path d="${polyPath(rectPts(ex, ey, ew, eh))}" fill="${shade(BARN, 0.12)}"/>`);
      o.push(
        `<path d="${polyPath(rectPts(x, y, w, h))}" fill="none" stroke="${alpha(TRIM, 0.35)}" stroke-width="1"/>`,
      );
    }
    return;
  }
  if (kind === 'silo') {
    const [cx, cy] = project(x + w / 2, y + h / 2);
    const r = TW * BUILDING_HEIGHT.silo.radius * w,
      ht = w > 0.7 ? BUILDING_HEIGHT.silo.tall : BUILDING_HEIGHT.silo.short;
    o.push(
      `<ellipse cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" rx="${r.toFixed(1)}" ry="${(r * 0.5).toFixed(1)}" fill="${shade(P.stone, 0.32)}"/>`,
    );
    o.push(
      `<path d="M${(cx - r).toFixed(1)} ${cy.toFixed(1)} v${-ht} a${r.toFixed(1)} ${(r * 0.5).toFixed(1)} 0 0 1 ${(r * 2).toFixed(1)} 0 v${ht} a${r.toFixed(1)} ${(r * 0.5).toFixed(1)} 0 0 1 ${(-r * 2).toFixed(1)} 0 z" fill="${tint(P.stone, SILO.tint)}"/>`,
    );
    o.push(
      `<path d="M${(cx - r).toFixed(1)} ${cy.toFixed(1)} v${-ht} a${r.toFixed(1)} ${(r * 0.5).toFixed(1)} 0 0 1 ${(r * 0.75).toFixed(1)} ${(-r * 0.42).toFixed(1)} v${ht} z" fill="${alpha('#ffffff', 0.26)}"/>`,
    );
    /* banded rings, then a domed cap */
    for (let i = 1; i < 4; i++)
      o.push(
        `<path d="M${(cx - r).toFixed(1)} ${(cy - (ht * i) / 4).toFixed(1)} a${r.toFixed(1)} ${(r * 0.5).toFixed(1)} 0 0 0 ${(r * 2).toFixed(1)} 0" fill="none" stroke="${alpha(shade(P.stone, SILO.band), 0.35)}" stroke-width="1"/>`,
      );
    o.push(
      `<path d="M${(cx - r).toFixed(1)} ${(cy - ht).toFixed(1)} a${r.toFixed(1)} ${(r * 0.95).toFixed(1)} 0 0 1 ${(r * 2).toFixed(1)} 0 a${r.toFixed(1)} ${(r * 0.5).toFixed(1)} 0 0 1 ${(-r * 2).toFixed(1)} 0 z" fill="${SLATE}"/>`,
    );
    o.push(
      `<path d="M${(cx - r).toFixed(1)} ${(cy - ht).toFixed(1)} a${r.toFixed(1)} ${(r * 0.95).toFixed(1)} 0 0 1 ${(r * 0.8).toFixed(1)} ${(-r * 0.62).toFixed(1)}" fill="none" stroke="${tint(SLATE, 0.3)}" stroke-width="2"/>`,
    );
    paintSiloMark(chain, cx, cy, ht, r, TRIM, o);
    return;
  }
  const barn = kind === 'barn';
  /* D268: a plot's shed steps up from a low lean-to of weathered boards, to timber, to
     stone. A shed with no style is the one it always was. */
  const wall = barn ? BARN : style ? STYLE_WALL[style] : P.wall;
  const he = BUILDING_HEIGHT[barn ? 'barn' : (tierStyle ?? 'shed')].wall;
  isoBox(x, y, w, h, he, tint(wall, 0.06), shade(wall, 0.24), shade(wall, 0.1), o);
  if (tierStyle && !barn) paintWallGrain(x, y, w, h, he, tierStyle, wall, o);
  /* white trim at the foot of the visible walls: the two that meet at the near corner. */
  const foot = rectPts(x, y, w, h, 0) as [Point, Point, Point, Point];
  const fc = frontCorner(x, y, w, h);
  const t1 = foot[(fc + 1) % 4] as Point,
    t2 = foot[fc] as Point,
    t3 = foot[(fc + 3) % 4] as Point;
  o.push(
    `<path d="M${t1[0].toFixed(1)} ${t1[1].toFixed(1)} L${t2[0].toFixed(1)} ${t2[1].toFixed(1)} L${t3[0].toFixed(1)} ${t3[1].toFixed(1)}" fill="none" stroke="${alpha(TRIM, 0.5)}" stroke-width="2"/>`,
  );

  const ov = 0.06;
  if (barn) {
    /* Gambrel: eave, knee, ridge. Only the +x slope and the +y gable are ever visible. */
    const r1 = 11,
      r2 = BUILDING_HEIGHT.barn.rise - r1;
    const prof: Array<[number, number]> = [
      [1 + ov, he],
      [0.78, he + r1],
      [0.5, he + r1 + r2],
    ];
    /* Which half of the roof and which gable end the viewer is on. At rest that is the
       +x slope and the +y gable; a quarter turn puts one or both of them round the back,
       and the half that faces away must not be the half that is drawn. */
    const xNear = towardEye(1, 0),
      yNear = towardEye(0, 1);
    const pt = (fx: number, z: number, yy: number): Point =>
      project(x + w * (xNear ? fx : 1 - fx), yy, z);
    const oy = w * ov * 0.4;
    /* yB is the gable end the viewer sees, yA the end behind it. */
    const yA = yNear ? y - oy : y + h + oy,
      yB = yNear ? y + h + oy : y - oy;
    const quad = (p1: [number, number], p2: [number, number], fill: string) =>
      o.push(
        `<path d="${polyPath([pt(p1[0], p1[1], yA), pt(p2[0], p2[1], yA), pt(p2[0], p2[1], yB), pt(p1[0], p1[1], yB)])}" fill="${fill}"/>`,
      );
    quad(prof[0] as [number, number], prof[1] as [number, number], shade(SLATE, BARN_ROOF.lower));
    quad(prof[1] as [number, number], prof[2] as [number, number], tint(SLATE, BARN_ROOF.upper));
    /* gable end, on the +y side */
    const g: Array<[number, number]> = [
      [1 + ov, he],
      [0.78, he + r1],
      [0.5, he + r1 + r2],
      [0.22, he + r1],
      [-ov, he],
      [-ov, 0],
      [1 + ov, 0],
    ];
    o.push(
      `<path d="${polyPath(g.map(([fx, z]) => pt(fx, z, yB)))}" fill="${shade(wall, 0.18)}"/>`,
    );
    /* the X-braced door the reference barns all carry */
    const dl = pt(0.3, 0, yB),
      dr = pt(0.7, 0, yB),
      dt = pt(0.7, he * 0.72, yB),
      dtl = pt(0.3, he * 0.72, yB);
    o.push(`<path d="${polyPath([dl, dr, dt, dtl])}" fill="${shade(wall, 0.36)}"/>`);
    o.push(
      `<path d="M${dl[0].toFixed(1)} ${dl[1].toFixed(1)} L${dt[0].toFixed(1)} ${dt[1].toFixed(1)} M${dr[0].toFixed(1)} ${dr[1].toFixed(1)} L${dtl[0].toFixed(1)} ${dtl[1].toFixed(1)}" stroke="${alpha(TRIM, 0.75)}" stroke-width="1.4"/>`,
    );
    o.push(
      `<path d="${polyPath([dl, dr, dt, dtl])}" fill="none" stroke="${alpha(TRIM, 0.75)}" stroke-width="1.2"/>`,
    );
    /* ridge highlight */
    const rA = pt(0.5, he + r1 + r2, yA),
      rB = pt(0.5, he + r1 + r2, yB);
    o.push(
      `<path d="M${rA[0].toFixed(1)} ${rA[1].toFixed(1)} L${rB[0].toFixed(1)} ${rB[1].toFixed(1)}" stroke="${tint(SLATE, 0.34)}" stroke-width="1.6"/>`,
    );
    if (P.S.frost)
      quad(prof[1] as [number, number], prof[2] as [number, number], alpha('#EFF1ED', 0.55));
    return;
  }
  /* shed: one pitch */
  const rise = BUILDING_HEIGHT[tierStyle ?? 'shed'].rise;
  const [a, b, c, d] = rectPts(x, y, w, h, he) as [Point, Point, Point, Point];
  const ridgeA = project(x + w / 2, y, he + rise),
    ridgeB = project(x + w / 2, y + h, he + rise);
  /* Both slopes are in view from above at every turn, but they change sides of the screen
     and the light does not follow them (D176): whichever runs away to the left is the lit
     one. At rest that is the -x slope, which is the one this has always tinted. */
  const xRight = project(x + w, y)[0] > project(x, y)[0];
  const litSlope = tint(SLATE, 0.16),
    darkSlope = shade(SLATE, 0.1);
  o.push(`<path d="${polyPath([d, a, ridgeA, ridgeB])}" fill="${xRight ? litSlope : darkSlope}"/>`);
  o.push(`<path d="${polyPath([c, b, ridgeA, ridgeB])}" fill="${xRight ? darkSlope : litSlope}"/>`);
  o.push(
    `<path d="M${ridgeA[0].toFixed(1)} ${ridgeA[1].toFixed(1)} L${ridgeB[0].toFixed(1)} ${ridgeB[1].toFixed(1)}" stroke="${tint(SLATE, 0.3)}" stroke-width="1.4"/>`,
  );
  if (P.S.frost) {
    o.push(
      `<path d="${polyPath([d, a, ridgeA, ridgeB])}" fill="${alpha('#EFF1ED', xRight ? 0.6 : 0.35)}"/>`,
    );
    o.push(
      `<path d="${polyPath([c, b, ridgeA, ridgeB])}" fill="${alpha('#EFF1ED', xRight ? 0.35 : 0.6)}"/>`,
    );
  }
  const dA = project(x, y + h * 0.64, 0),
    dB = project(x, y + h * 0.36, 0);
  o.push(
    `<path d="M${dA[0].toFixed(1)} ${dA[1].toFixed(1)} L${dB[0].toFixed(1)} ${dB[1].toFixed(1)} L${dB[0].toFixed(1)} ${(dB[1] - he * 0.64).toFixed(1)} L${dA[0].toFixed(1)} ${(dA[1] - he * 0.64).toFixed(1)} Z" fill="${shade(P.soil, 0.34)}"/>`,
  );
  paintShedMark(chain, x, y, w, h, he, SLATE, wall, TRIM, o);
}

/**
 * D346: a chain building's own silhouette, beyond its roof tint (D343): a cowl, a still, a
 * stack of bales for the grain-handling ones, which are silos.
 */
function paintSiloMark(
  chain: string | undefined,
  cx: number,
  cy: number,
  ht: number,
  r: number,
  TRIM: string,
  o: string[],
): void {
  if (chain === 'maltings') {
    // The oast's own white cowl, two tiers and a tilted cap, well clear of the roofline.
    const top = cy - ht - r * 1.3;
    o.push(
      `<path d="${polyPath([
        [cx - r * 0.55, top + r * 0.35],
        [cx + r * 0.55, top + r * 0.35],
        [cx, top - r * 0.15],
      ])}" fill="${TRIM}"/>`,
    );
    o.push(
      `<path d="${polyPath([
        [cx - r * 0.3, top - r * 0.15],
        [cx + r * 0.6, top - r * 0.3],
        [cx + r * 0.15, top - r * 1.1],
      ])}" fill="${TRIM}"/>`,
    );
  } else if (chain === 'distillery') {
    // The still's own copper pot, prominent against the foot, and its swan neck.
    const copper = '#B5703A';
    const [px, py] = [cx + r * 1.5, cy + r * 0.6];
    o.push(
      `<circle cx="${px.toFixed(1)}" cy="${py.toFixed(1)}" r="${(r * 0.72).toFixed(1)}" fill="${copper}"/>`,
    );
    o.push(
      `<path d="M${(px + r * 0.55).toFixed(1)} ${py.toFixed(1)} l${(r * 1.1).toFixed(1)} ${(-r * 0.28).toFixed(1)}" stroke="${copper}" stroke-width="${(r * 0.4).toFixed(1)}" stroke-linecap="round"/>`,
    );
  } else if (chain === 'feed_store') {
    // Bales against the foot, stacked, waiting to go in.
    for (const [bx, by] of [
      [cx + r * 1.3, cy + r * 0.7],
      [cx + r * 1.3, cy + r * 0.1],
    ] as const)
      o.push(
        `<ellipse cx="${bx.toFixed(1)}" cy="${by.toFixed(1)}" rx="${(r * 0.55).toFixed(1)}" ry="${(r * 0.36).toFixed(1)}" fill="${BALE.body}"/>`,
      );
  }
}

/** D346: see `paintSiloMark`; the sheds' own marks. */
function paintShedMark(
  chain: string | undefined,
  x: number,
  y: number,
  w: number,
  h: number,
  he: number,
  SLATE: string,
  wall: string,
  TRIM: string,
  o: string[],
): void {
  if (!chain) return;
  const front = project(x + w / 2, y + h);
  // A tall chimney well clear of the ridge, with two good puffs: big enough to break the
  // roofline, which is what tells it apart at a glance.
  const chimney = (dx: number, colour: string) => {
    const [cx, cy] = project(x + w / 2 + dx, y + h * 0.3, he * 1.7);
    o.push(
      `<path d="${polyPath([
        [cx - 3.5, cy],
        [cx + 3.5, cy],
        [cx + 3.5, cy - 18],
        [cx - 3.5, cy - 18],
      ])}" fill="${shade(wall, 0.32)}"/>`,
    );
    o.push(
      `<circle cx="${cx.toFixed(1)}" cy="${(cy - 25).toFixed(1)}" r="7" fill="${alpha(colour, 0.75)}"/>`,
    );
    o.push(
      `<circle cx="${(cx + 3).toFixed(1)}" cy="${(cy - 36).toFixed(1)}" r="9" fill="${alpha(colour, 0.65)}"/>`,
    );
  };
  switch (chain) {
    case 'smokehouse':
      chimney(w * 0.26, SMOKE.colour);
      return;
    case 'trout_smokery':
      chimney(w * 0.26, mix(SMOKE.colour, '#8CA0A6', 0.4));
      return;
    case 'bakery':
      chimney(-w * 0.26, SMOKE.colour);
      return;
    case 'crisp_works':
      // The fryer's own chimney, a paler steam rather than wood smoke, and a sack going out.
      chimney(w * 0.26, mix(SMOKE.colour, '#ffffff', 0.5));
      o.push(
        (() => {
          const [sx, sy] = project(x + w / 2 - 0.2 * w, y + h);
          return `<ellipse cx="${sx.toFixed(1)}" cy="${sy.toFixed(1)}" rx="9" ry="6" fill="#B9935C"/>`;
        })(),
      );
      return;
    case 'jam_kitchen': {
      // The copper's own chimney, and the jars going out.
      chimney(-w * 0.26, mix(SMOKE.colour, '#D98A8A', 0.3));
      for (const dx of [-0.14, 0.12]) {
        const [jx, jy] = project(x + w / 2 + dx * w, y + h);
        o.push(
          `<rect x="${(jx - 5).toFixed(1)}" y="${(jy - 9).toFixed(1)}" width="10" height="9" fill="#8C2F3A"/>`,
        );
      }
      return;
    }
    case 'packhouse': {
      const [gx, gy] = project(x + w / 2, y + h, he * 0.58);
      o.push(
        `<rect x="${(gx - w * 19).toFixed(1)}" y="${(gy - he * 0.37).toFixed(1)}" width="${(w * 38).toFixed(1)}" height="${(he * 0.68).toFixed(1)}" fill="${alpha(GLASSHOUSE.glass, 0.85)}"/>`,
      );
      return;
    }
    case 'winery': {
      // Vats taller than the door, so they read above the hedge.
      for (const dx of [-0.26, 0.26]) {
        const [vx, vy] = project(x + w / 2 + dx * w, y + h, he * 0.3);
        o.push(
          `<rect x="${(vx - 7).toFixed(1)}" y="${(vy - 14).toFixed(1)}" width="14" height="14" rx="3" fill="${VINE.grapes}"/>`,
        );
      }
      return;
    }
    case 'creamery': {
      for (const dx of [-0.2, 0.2]) {
        const [cx, cy] = project(x + w / 2 + dx * w, y + h, he * 0.2);
        o.push(
          `<rect x="${(cx - 5).toFixed(1)}" y="${(cy - 10).toFixed(1)}" width="10" height="10" rx="2" fill="#DCE3E6"/>`,
        );
      }
      return;
    }
    case 'drying_shed': {
      const [lx, ly] = front;
      for (let i = 0; i < 5; i++)
        o.push(
          `<path d="M${(lx - w * 17).toFixed(1)} ${(ly - he * (0.1 + i * 0.16)).toFixed(1)} h${(w * 34).toFixed(1)}" stroke="${shade(wall, 0.3)}" stroke-width="2.4"/>`,
        );
      return;
    }
    case 'honey_house': {
      for (const dx of [-0.22, 0.22]) {
        const [hx, hy] = project(x + w / 2 + dx * w, y + h);
        o.push(
          `<rect x="${(hx - 7).toFixed(1)}" y="${(hy - 15).toFixed(1)}" width="14" height="15" fill="${HIVE.body}" stroke="${HIVE.band}" stroke-width="1.4"/>`,
        );
      }
      return;
    }
    case 'veg_box_shed':
    case 'egg_packing_shed': {
      // Stacked two high, taller than one crate alone, so the two read apart by height.
      const colour = chain === 'veg_box_shed' ? '#6E8A4C' : '#E8DEC0';
      for (const [dx, dz, n] of [
        [-0.2, 0, 1],
        [0.16, 0, 1],
        [-0.02, -0.08, 2],
      ] as const)
        for (let i = 0; i < n; i++) {
          const [bx, by] = project(x + w / 2 + dx * w, y + h + dz);
          o.push(
            `<rect x="${(bx - 7).toFixed(1)}" y="${(by - 7 - i * 9).toFixed(1)}" width="14" height="9" fill="${colour}"/>`,
          );
        }
      return;
    }
    case 'cress_packhouse': {
      const [tx, ty] = front;
      o.push(
        `<rect x="${(tx - w * 17).toFixed(1)}" y="${(ty - 5).toFixed(1)}" width="${(w * 34).toFixed(1)}" height="8" fill="${mix(SLATE, '#4E97BE', 0.5)}"/>`,
      );
      return;
    }
    default:
      return;
  }
}

/**
 * D268: the grain of a styled wall on its two near faces: boards up a lean-to and a timber
 * shed, courses across stone. Screen maths only, so it holds at every turn.
 */
function paintWallGrain(
  x: number,
  y: number,
  w: number,
  h: number,
  he: number,
  style: BuildingStyle,
  wall: string,
  o: string[],
): void {
  if (isPlan()) return;
  const foot = rectPts(x, y, w, h, 0) as [Point, Point, Point, Point];
  const fc = frontCorner(x, y, w, h);
  const near = foot[fc] as Point;
  const sides = [foot[(fc + 1) % 4] as Point, foot[(fc + 3) % 4] as Point];
  const line = shade(wall, style === 'stone' ? 0.3 : 0.38);
  const g: string[] = [];
  const f = (n: number) => n.toFixed(1);
  for (const side of sides) {
    if (style === 'stone') {
      for (let z = he / 4; z < he; z += he / 4)
        g.push(`M${f(side[0])} ${f(side[1] - z)} L${f(near[0])} ${f(near[1] - z)}`);
    } else {
      const n = style === 'lean_to' ? 5 : 7;
      for (let i = 1; i < n; i++) {
        const px = side[0] + ((near[0] - side[0]) * i) / n,
          py = side[1] + ((near[1] - side[1]) * i) / n;
        g.push(`M${f(px)} ${f(py)} l0 ${f(-he)}`);
      }
    }
  }
  o.push(`<path d="${g.join(' ')}" stroke="${line}" stroke-width="0.7" fill="none" opacity=".7"/>`);
}

/**
 * D268: a hand at work on a plot: a small figure in a smock and a straw hat, a hoe over the
 * shoulder. Drawn at the animals' scale. From above, the hat.
 */
function paintWorker(wx: number, wy: number, o: string[], look?: string): void {
  /* D331: the clothes a worker decoration puts on the hands. */
  const W = { ...WORKER, ...workerLook(look) };
  const [cx, cy] = project(wx, wy);
  const f = (n: number) => n.toFixed(1);
  if (isPlan()) {
    o.push(`<circle cx="${f(cx)}" cy="${f(cy)}" r="2.2" fill="${W.hat}"/>`);
    return;
  }
  o.push(`<ellipse cx="${f(cx)}" cy="${f(cy)}" rx="2.6" ry="1" fill="rgba(42,36,24,.24)"/>`);
  o.push(
    `<path d="M${f(cx - 0.8)} ${f(cy)} v-2.6 M${f(cx + 0.8)} ${f(cy)} v-2.6" stroke="${W.legs}" stroke-width="0.9"/>`,
  );
  o.push(`<path d="M${f(cx - 1.8)} ${f(cy - 2.4)} l0.6 -4.4 h2.4 l0.6 4.4 z" fill="${W.smock}"/>`);
  o.push(`<circle cx="${f(cx)}" cy="${f(cy - 7.8)}" r="1.2" fill="${WORKER.skin}"/>`);
  o.push(
    `<ellipse cx="${f(cx)}" cy="${f(cy - 8.6)}" rx="2.2" ry="0.6" fill="${W.hat}"/><path d="M${f(cx - 1)} ${f(cy - 8.6)} q1 -1.6 2 0" fill="${W.hat}"/>`,
  );
  o.push(
    `<path d="M${f(cx + 1.6)} ${f(cy - 2)} l2 -7.6" stroke="${WORKER.handle}" stroke-width="0.6"/><path d="M${f(cx + 3.6)} ${f(cy - 9.6)} l1.4 0.4" stroke="${WORKER.blade}" stroke-width="0.9"/>`,
  );
}

/**
 * D268: the tier sign at a plot's front corner: a post and a small board with the last
 * milestone reached, in the map's ink on its trim.
 */
function paintTierSign(wx: number, wy: number, text: string, o: string[]): void {
  const [cx, cy] = project(wx, wy);
  const f = (n: number) => n.toFixed(1);
  const w = 5 + text.length * 4.4;
  const lift = isPlan() ? 0 : 12;
  if (!isPlan())
    o.push(
      `<path d="M${f(cx)} ${f(cy)} v-${lift - 4}" stroke="${TIER_SIGN.post}" stroke-width="1.4"/>`,
    );
  o.push(
    `<g data-testid="tier-sign"><rect x="${f(cx - w / 2)}" y="${f(cy - lift - 4)}" width="${f(w)}" height="9" rx="1.6" fill="${TIER_SIGN.board}" stroke="${TIER_SIGN.post}" stroke-width="0.9"/>` +
      `<text x="${f(cx)}" y="${f(cy - lift + 2.8)}" text-anchor="middle" font-size="7.4" font-weight="700" font-family="system-ui, sans-serif" fill="${TIER_SIGN.ink}">${text}</text></g>`,
  );
}

/**
 * D269: a plot cut faster than its ripening can be drawn shows its work going on: chaff
 * rising off the rows on a steady loop. Its own element, so the loop runs across repaints.
 */
function paintBusy(pts: Point[], o: string[]): void {
  pts.forEach(([wx, wy], i) => {
    const [cx, cy] = project(wx, wy);
    o.push(
      `<circle class="map-busy-bit" cx="${cx.toFixed(1)}" cy="${(cy - 6).toFixed(1)}" r="1.3" fill="${CROP_PARTS.chaff}" style="animation-delay:${((i * 0.37) % 1.6).toFixed(2)}s"/>`,
    );
  });
}

/**
 * D268: a milestone reached, played on the plot itself: a ring of light opening out over
 * it and a scatter of sparks. Its own element, so it plays once and is not cut short.
 */
function paintBurst(wx: number, wy: number, r: number, o: string[]): void {
  const [cx, cy] = project(wx, wy);
  const rx = r * TW * 0.9,
    ry = rx / 2;
  o.push(
    `<ellipse class="map-burst-ring" cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" rx="${rx.toFixed(1)}" ry="${ry.toFixed(1)}" fill="none" stroke="${BURST}" stroke-width="3"/>`,
  );
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    o.push(
      `<circle class="map-burst-spark" cx="${(cx + Math.cos(a) * rx * 0.6).toFixed(1)}" cy="${(cy + Math.sin(a) * ry * 0.6 - 10).toFixed(1)}" r="2" fill="${BURST}" style="animation-delay:${(i * 0.05).toFixed(2)}s"/>`,
    );
  }
}

/** The farmhouse's wall height and roof rise, in the same units as a shed's. */
const HOUSE_WALL = BUILDING_HEIGHT.house.wall,
  HOUSE_RISE = BUILDING_HEIGHT.house.rise;
/** How far the house stands in from its tile, so it reads as a house on a yard. */
const HOUSE_INSET = 0.1;

/** D381: a country tree's leaf: the season's wood towards its kind's own, a little hazed. */
export function countryLeaf(P: Palette, kind: CountryTreeKind, j: number): string {
  const look = COUNTRY_TREE[kind];
  const own = mix(P.wood, look.leaf, look.mix);
  const leaf = mix(own, P.far(own), COUNTRY_TREE.haze);
  const d = (j - 0.5) * 2 * COUNTRY_TREE.tone.jitter;
  return d >= 0 ? tint(leaf, d) : shade(leaf, -d);
}

/**
 * D199: where the farmhouse's chimney stands, in world units: on the ridge towards the -y
 * end. Fixed to the world rather than the turn, so the smoke and the stack always agree.
 */
export function houseChimney(
  x: number,
  y: number,
  w: number,
  h: number,
): { x: number; y: number; s: number; top: number } {
  const s = 0.17 * Math.min(w, h);
  return { x: x + w * 0.5 + s * 0.15, y: y + h * 0.28, s, top: HOUSE_WALL + HOUSE_RISE + 5 };
}

/**
 * D199: the farmhouse. Pale walls, a steep pitched roof in the home tile colour with its
 * ridge running along y, a brick chimney on the ridge, a door in the gable end facing the
 * viewer and a window in the side wall. From above it is the roof, the ridge and the stack.
 */
function paintHouse(
  x0: number,
  y0: number,
  w0: number,
  h0: number,
  P: Palette,
  o: string[],
  ROOF: string,
  TRIM: string,
): void {
  const x = x0 + HOUSE_INSET,
    y = y0 + HOUSE_INSET,
    w = w0 - HOUSE_INSET * 2,
    h = h0 - HOUSE_INSET * 2;
  const ov = 0.07;
  const ch = houseChimney(x0, y0, w0, h0);
  const BRICK = shade(P.barn, CHIMNEY_BRICK_SHADE);
  if (isPlan()) {
    o.push(
      `<path d="${polyPath(rectPts(x - ov, y - ov, w + ov * 2, h + ov * 2))}" fill="${ROOF}" stroke="${shade(ROOF, 0.32)}" stroke-width="1"/>`,
    );
    /* The two slopes meet at a ridge along y; the -x half is the lit one. */
    o.push(
      `<path d="${polyPath(rectPts(x - ov, y - ov, w / 2 + ov, h + ov * 2))}" fill="${tint(ROOF, 0.12)}"/>`,
    );
    const a = project(x + w / 2, y - ov),
      b = project(x + w / 2, y + h + ov);
    o.push(
      `<path d="M${a[0].toFixed(1)} ${a[1].toFixed(1)} L${b[0].toFixed(1)} ${b[1].toFixed(1)}" stroke="${tint(ROOF, 0.34)}" stroke-width="1.8" stroke-linecap="round"/>`,
    );
    o.push(
      `<path d="${polyPath(rectPts(ch.x - ch.s / 2, ch.y - ch.s / 2, ch.s, ch.s))}" fill="${BRICK}" stroke="${shade(BRICK, 0.35)}" stroke-width="1"/>`,
    );
    return;
  }
  const wall = P.wall;
  const he = HOUSE_WALL,
    rise = HOUSE_RISE;
  const wallLeft = shade(wall, 0.24),
    wallRight = shade(wall, 0.1);
  isoBox(x, y, w, h, he, tint(wall, 0.06), wallLeft, wallRight, o);
  /* The two visible walls meet at the front corner. The one running along x (corners 0-1
     or 2-3) is a gable end; the one running along y is a side under the eaves. */
  const f = frontCorner(x, y, w, h);
  const gableIsLeft = f % 2 === 0;
  const gableFill = gableIsLeft ? wallLeft : wallRight;
  const sideFill = gableIsLeft ? wallRight : wallLeft;
  const gy = f === 2 || f === 3 ? y + h : y;
  const sx = f === 1 || f === 2 ? x + w : x;
  o.push(
    `<path d="${polyPath([project(x, gy, he), project(x + w, gy, he), project(x + w / 2, gy, he + rise)])}" fill="${gableFill}"/>`,
  );
  /* A door in the gable end and a window in the side, both trimmed. */
  const door: Point[] = [
    project(x + w * 0.4, gy, 0),
    project(x + w * 0.6, gy, 0),
    project(x + w * 0.6, gy, he * 0.64),
    project(x + w * 0.4, gy, he * 0.64),
  ];
  o.push(
    `<path d="${polyPath(door)}" fill="${shade(P.barn, 0.3)}" stroke="${alpha(TRIM, 0.7)}" stroke-width="1"/>`,
  );
  const win: Point[] = [
    project(sx, y + h * 0.34, he * 0.36),
    project(sx, y + h * 0.66, he * 0.36),
    project(sx, y + h * 0.66, he * 0.74),
    project(sx, y + h * 0.34, he * 0.74),
  ];
  o.push(
    `<path d="${polyPath(win)}" fill="${mix(P.water, sideFill, 0.35)}" stroke="${alpha(TRIM, 0.85)}" stroke-width="1.2"/>`,
  );
  /* The roof: two slopes to a ridge along y, overhanging a little all round. Whichever
     slope runs away to the left of the screen is the lit one, as on a shed (D176). */
  const [a, b, c, d] = rectPts(x - ov, y - ov, w + ov * 2, h + ov * 2, he) as [
    Point,
    Point,
    Point,
    Point,
  ];
  const ridgeA = project(x + w / 2, y - ov, he + rise),
    ridgeB = project(x + w / 2, y + h + ov, he + rise);
  const xRight = project(x + w, y)[0] > project(x, y)[0];
  const lit = tint(ROOF, 0.16),
    dark = shade(ROOF, 0.12);
  const slopeA = polyPath([d, a, ridgeA, ridgeB]),
    slopeB = polyPath([c, b, ridgeA, ridgeB]);
  o.push(`<path d="${slopeA}" fill="${xRight ? lit : dark}"/>`);
  o.push(`<path d="${slopeB}" fill="${xRight ? dark : lit}"/>`);
  o.push(
    `<path d="M${ridgeA[0].toFixed(1)} ${ridgeA[1].toFixed(1)} L${ridgeB[0].toFixed(1)} ${ridgeB[1].toFixed(1)}" stroke="${tint(ROOF, 0.34)}" stroke-width="1.6"/>`,
  );
  if (P.S.frost) {
    o.push(`<path d="${slopeA}" fill="${alpha('#EFF1ED', xRight ? 0.6 : 0.35)}"/>`);
    o.push(`<path d="${slopeB}" fill="${alpha('#EFF1ED', xRight ? 0.35 : 0.6)}"/>`);
  }
  /* The chimney, a brick stack on the ridge with its foot sunk into the roof. */
  paintStack(ch.x - ch.s / 2, ch.y - ch.s / 2, ch.s, he + rise * 0.7, ch.top, BRICK, o);
}

/** A small box standing from `z0` to `z1`: the chimney, whose foot is hidden in the roof. */
function paintStack(
  x: number,
  y: number,
  s: number,
  z0: number,
  z1: number,
  fill: string,
  o: string[],
): void {
  const up = rectPts(x, y, s, s, z1) as [Point, Point, Point, Point];
  const dn = rectPts(x, y, s, s, z0) as [Point, Point, Point, Point];
  const f = frontCorner(x, y, s, s);
  const l = up[(f + 1) % 4] as Point,
    r = up[(f + 3) % 4] as Point,
    lb = dn[(f + 1) % 4] as Point,
    rb = dn[(f + 3) % 4] as Point,
    fu = up[f] as Point,
    fd = dn[f] as Point;
  o.push(`<path d="${polyPath([l, fu, fd, lb])}" fill="${shade(fill, 0.22)}"/>`);
  o.push(`<path d="${polyPath([fu, r, rb, fd])}" fill="${fill}"/>`);
  o.push(`<path d="${polyPath(up)}" fill="${shade(fill, 0.45)}"/>`);
}

/** D196: the zoom from which a building's sign can be read, and so is shown. */
export const SIGN_ZOOM = 1;

/* D196: one glyph per use, drawn in a 9-unit box round the sign's centre. Stroked marks in
   the map's own line: a sack for a store, a horseshoe for stock, a cog for a works, a
   hammer for the farm's own plant, a coin for a sale. */
const SIGN_GLYPH: Record<BuildingUse, string> = {
  /* A sack slumped on its base, the neck tied off below two ears of cloth. */
  storage:
    '<path d="M-1.1 -1.5 C-3.9 -0.4 -4.3 3.7 -2.2 3.7 L2.2 3.7 C4.3 3.7 3.9 -0.4 1.1 -1.5 Z" fill="currentColor"/><path d="M-1.4 -1.95 H1.4" stroke="currentColor" stroke-width="1" stroke-linecap="round"/><path d="M-0.3 -2.4 L-1.9 -3.9 L-0.1 -3.4 Z M0.3 -2.4 L1.9 -3.9 L0.1 -3.4 Z" fill="currentColor"/>',
  /* A horseshoe: heels flared out and nail holes through the iron, so it is not a letter. */
  livestock:
    '<path d="M-2.6 -3.1 L-2.6 0.2 A2.6 2.6 0 0 0 2.6 0.2 L2.6 -3.1" fill="none" stroke="currentColor" stroke-width="2.2"/><path d="M-4 -3.6 L-1.8 -3.6 M1.8 -3.6 L4 -3.6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><g fill="SIGN_BG"><circle cx="-2.6" cy="-1.7" r="0.45"/><circle cx="-2.2" cy="1.5" r="0.45"/><circle cx="2.2" cy="1.5" r="0.45"/><circle cx="2.6" cy="-1.7" r="0.45"/></g>',
  processing:
    '<circle r="2.2" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M0 -3.8 V-2.3 M0 3.8 V2.3 M-3.8 0 H-2.3 M3.8 0 H2.3 M-2.7 -2.7 L-1.6 -1.6 M2.7 2.7 L1.6 1.6 M-2.7 2.7 L-1.6 1.6 M2.7 -2.7 L1.6 -1.6" stroke="currentColor" stroke-width="1.4"/>',
  infrastructure:
    '<path d="M-3 3.2 L1.2 -1" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M-0.6 -3.4 L3.4 0.6" stroke="currentColor" stroke-width="2.4"/>',
  /* D199: a house: a pitched roof over a wall, a door in it, and a chimney on the roof. */
  home: '<path d="M1.6 -3.6 H2.9 V-1.5 L1.6 -2.7 Z" fill="currentColor"/><path d="M-3.8 -0.2 L0 -3.8 L3.8 -0.2 V3.6 H-3.8 Z" fill="currentColor"/><rect x="-0.9" y="1" width="1.8" height="2.6" fill="SIGN_BG"/>',
  sales:
    '<circle r="3.4"fill="none" stroke="currentColor" stroke-width="1.2"/><path d="M1.2 -1.1 A1.6 1.6 0 1 0 1.2 1.1" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>',
};

/**
 * D196: a small board on the building saying what it is for, in the roof's own colour on
 * the trim. It faces the viewer in either projection: on the roof from above, and at the
 * roof's middle height in the isometric view, below where the power flag stands. It is
 * painted always and hidden by the `.map-sign` rule until the map is close enough to read
 * it, so zooming never repaints the world.
 */
function paintSign(
  x: number,
  y: number,
  w: number,
  h: number,
  P: Palette,
  kind: BuildingKind,
  use: BuildingUse,
  o: string[],
): void {
  const roof = P.roofs[use];
  const lift = isPlan()
    ? 0
    : kind === 'barn'
      ? 40
      : kind === 'silo'
        ? 38
        : kind === 'house'
          ? HOUSE_WALL + HOUSE_RISE * 0.45
          : 29;
  const [cx, cy] = project(x + w / 2, y + h / 2, lift);
  o.push(
    `<g class="map-sign" data-testid="iso-sign" data-use="${use}" transform="translate(${cx.toFixed(1)} ${cy.toFixed(1)})" color="${shade(roof, 0.28)}">` +
      `<rect x="-7" y="-6" width="14" height="12" rx="2.5" fill="${P.trim}" stroke="${shade(roof, 0.36)}" stroke-width="1"/>` +
      SIGN_GLYPH[use].replace(/SIGN_BG/g, P.trim) +
      `</g>`,
  );
}

/**
 * D239: a timber bridge over one water tile, in the map's own box language (D168): a low
 * deck standing a little proud of the banks, planks across it and a rail down each side.
 * From above it is the deck and its rails, as every box is its top (D178).
 */
function paintBridge(tx: number, ty: number, along: 'x' | 'y', P: Palette, o: string[]): void {
  const DECK = mix(P.fence, '#D8C29A', 0.35);
  const W = 0.56,
    OVER = 0.12,
    H = 4;
  const x = along === 'y' ? tx + (1 - W) / 2 : tx - OVER;
  const y = along === 'y' ? ty - OVER : ty + (1 - W) / 2;
  const w = along === 'y' ? W : 1 + OVER * 2;
  const h = along === 'y' ? 1 + OVER * 2 : W;
  isoBox(x, y, w, h, H, DECK, shade(DECK, 0.34), shade(DECK, 0.18), o);
  const z = isPlan() ? 0 : H;
  const line = (
    ax: number,
    ay: number,
    bx: number,
    by: number,
    lift: number,
    c: string,
    sw: number,
  ) => {
    const A = project(ax, ay, lift),
      B = project(bx, by, lift);
    o.push(
      `<path d="M${A[0].toFixed(1)} ${A[1].toFixed(1)} L${B[0].toFixed(1)} ${B[1].toFixed(1)}" stroke="${c}" stroke-width="${sw}" stroke-linecap="round"/>`,
    );
  };
  // Planks across the deck.
  const n = 6;
  for (let i = 1; i < n; i++) {
    const t = i / n;
    if (along === 'y') line(x, y + h * t, x + w, y + h * t, z, alpha(shade(DECK, 0.4), 0.55), 0.6);
    else line(x + w * t, y, x + w * t, y + h, z, alpha(shade(DECK, 0.4), 0.55), 0.6);
  }
  // A rail down each side, standing on the deck edge.
  const rail = isPlan() ? 0 : H + 5;
  const RAIL = shade(P.fence, 0.2);
  if (along === 'y') {
    line(x, y, x, y + h, rail, RAIL, 1.4);
    line(x + w, y, x + w, y + h, rail, RAIL, 1.4);
  } else {
    line(x, y, x + w, y, rail, RAIL, 1.4);
    line(x, y + h, x + w, y + h, rail, RAIL, 1.4);
  }
}

/**
 * Something standing on the land. `x` and `y` are only its sort anchor; a fence carries
 * the edge it runs along, a building its own rectangle.
 */
export type Prop =
  | { k: 'tree'; x: number; y: number; s: number; kind: TreeKind }
  | { k: 'animal'; x: number; y: number; kind: AnimalKind; breed?: number }
  | {
      k: 'build';
      x: number;
      y: number;
      w: number;
      h: number;
      kind: BuildingKind;
      use?: BuildingUse;
      /** D268: a plot's building steps up from lean-to to timber to stone. */
      style?: BuildingStyle;
      /** D291: the building decoration on show. */
      look?: string;
      /** D343: a chain building's own id, for its own roof (`CHAIN_ROOF`). */
      chain?: string;
    }
  | {
      k: 'fence';
      x: number;
      y: number;
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      /** D291: the fence decoration on show. */
      style?: string;
    }
  | { k: 'bale'; x: number; y: number }
  | { k: 'rock'; x: number; y: number; s: number }
  | { k: 'reed'; x: number; y: number }
  | { k: 'hive'; x: number; y: number }
  | { k: 'mark'; x: number; y: number; kind: string; fresh?: boolean }
  | {
      k: 'memorial';
      x: number;
      y: number;
      form: MemorialForm;
      material: string;
      size: number;
      w?: number;
      h?: number;
    }
  | { k: 'vine'; x: number; y: number }
  | { k: 'hop'; x: number; y: number }
  | { k: 'worker'; x: number; y: number; look?: string }
  | { k: 'sign'; x: number; y: number; text: string }
  | { k: 'busy'; x: number; y: number; pts: Point[] }
  | { k: 'burst'; x: number; y: number; r: number }
  | { k: 'crocus'; x: number; y: number }
  | { k: 'bridge'; x: number; y: number; tx: number; ty: number; along: 'x' | 'y' }
  /** D300: tomatoes, grown under glass; `tier` grows the frame with its milestones (D268). */
  | { k: 'glasshouse'; x: number; y: number; tier?: number }
  /** D301: a trout now and then, just under the water. */
  | { k: 'fish'; x: number; y: number }
  /** D345: a crop waiting to be tapped, no hand and its bar full. */
  | { k: 'ready'; x: number; y: number; id: string; icon: string }
  /** D348: a theme kit's own standing thing on its footprint, which only its kit paints. */
  | { k: 'piece'; x: number; y: number; w: number; h: number; unit: unknown };

export function paintProp(s: Prop, P: Palette, o: string[]): void {
  if (s.k === 'tree') paintTree(s.x, s.y, s.s, P, s.kind, o);
  else if (s.k === 'animal') {
    /* D259: animals at twice their old size, so a hen reads at phone size. */
    const [cx, cy] = project(s.x, s.y);
    scaleAbout(cx, cy, ANIMAL_SCALE, o, (q) => paintAnimal(s.x, s.y, s.kind, P, q, s.breed));
  } else if (s.k === 'build')
    paintBuilding(s.x, s.y, s.w, s.h, P, s.kind, o, s.use, s.style, s.look, s.chain);
  else if (s.k === 'worker') {
    const [cx, cy] = project(s.x, s.y);
    scaleAbout(cx, cy, ANIMAL_SCALE, o, (q) => paintWorker(s.x, s.y, q, s.look));
  } else if (s.k === 'sign') paintTierSign(s.x, s.y, s.text, o);
  else if (s.k === 'busy') paintBusy(s.pts, o);
  else if (s.k === 'burst') paintBurst(s.x, s.y, s.r, o);
  else if (s.k === 'fence') paintFence(s.x1, s.y1, s.x2, s.y2, P, o, s.style);
  else if (s.k === 'bridge') paintBridge(s.tx, s.ty, s.along, P, o);
  else if (s.k === 'bale') {
    const [cx, cy] = project(s.x, s.y);
    /* D178: a round bale from above is the barrel of it, with the twine across. */
    if (isPlan()) {
      o.push(
        `<rect x="${(cx - 5.5).toFixed(1)}" y="${(cy - 4.5).toFixed(1)}" width="11" height="9" rx="4" fill="${BALE.body}"/>`,
      );
      o.push(
        `<path d="M${(cx - 5).toFixed(1)} ${cy.toFixed(1)} L${(cx + 5).toFixed(1)} ${cy.toFixed(1)}" stroke="${alpha(BALE.core, 0.7)}" stroke-width="1.1"/>`,
      );
      return;
    }
    o.push(
      `<ellipse cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" rx="7" ry="2.8" fill="${SHADOW}"/>`,
    );
    o.push(
      `<rect x="${(cx - 5.5).toFixed(1)}" y="${(cy - 10).toFixed(1)}" width="11" height="9" rx="4.5" fill="${BALE.body}"/>`,
    );
    o.push(
      `<ellipse cx="${(cx - 5.5).toFixed(1)}" cy="${(cy - 5.5).toFixed(1)}" rx="2.4" ry="4.5" fill="${BALE.end}"/>`,
    );
    o.push(
      `<ellipse cx="${(cx - 5.5).toFixed(1)}" cy="${(cy - 5.5).toFixed(1)}" rx="1.1" ry="2.2" fill="${BALE.core}"/>`,
    );
  } else if (s.k === 'rock') {
    const [cx, cy] = project(s.x, s.y);
    /* A stone from above: the stone itself, over its own point, and no shadow. */
    const lift = isPlan() ? 0 : s.s * 0.55;
    if (!isPlan())
      o.push(
        `<ellipse cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" rx="${(s.s * 1.15).toFixed(1)}" ry="${(s.s * 0.45).toFixed(1)}" fill="rgba(42,36,24,.20)"/>`,
      );
    o.push(
      `<circle cx="${cx.toFixed(1)}" cy="${(cy - lift).toFixed(1)}" r="${s.s.toFixed(1)}" fill="${P.stone}"/>`,
    );
    o.push(
      `<path d="M${(cx - s.s).toFixed(1)} ${(cy - lift).toFixed(1)} a${s.s} ${s.s} 0 0 1 ${(s.s * 1.1).toFixed(1)} ${(-s.s * 0.75).toFixed(1)}" fill="none" stroke="${alpha('#ffffff', 0.3)}" stroke-width="1.6"/>`,
    );
  } else if (s.k === 'hive') paintHive(s.x, s.y, o);
  else if (s.k === 'glasshouse') paintGlasshouse(s.x, s.y, s.tier ?? 0, o);
  else if (s.k === 'fish') paintFish(s.x, s.y, o);
  else if (s.k === 'ready') paintReady(s.x, s.y, s.id, s.icon, o);
  else if (s.k === 'memorial') paintMemorial(s, P, o);
  else if (s.k === 'mark') {
    /* D259: a festival's mark stands about as tall as the barn beside it. */
    const [cx, cy] = project(s.x, s.y);
    scaleAbout(cx, cy, MARK_SCALE, o, (q) => {
      if (s.kind === 'yew_tree') paintTree(s.x, s.y, 16, P, 'wood', q);
      else paintMark(s.kind, s.x, s.y, q);
    });
  } else if (s.k === 'vine') paintVine(s.x, s.y, o);
  else if (s.k === 'hop') paintHop(s.x, s.y, o);
  else if (s.k === 'crocus') paintCrocus(s.x, s.y, o);
  else if (s.k === 'reed') {
    const [cx, cy] = project(s.x, s.y);
    /* Reeds from above splay round their own point instead of standing out of the water. */
    const arms = isPlan() ? 'l-3 -2 M0 0 l1 3.4 M0 0 l3.2 -1.4' : 'l-2 -7 M0 0 l1 -8 M0 0 l3 -6';
    const at = `${cx.toFixed(1)} ${cy.toFixed(1)}`;
    o.push(
      `<path d="M${at} ${arms.replace(/M0 0/g, `M${at}`)}" stroke="${alpha(shade(P.wood, 0.1), 0.65)}" stroke-width="1.2" fill="none" stroke-linecap="round"/>`,
    );
  }
}

/**
 * Standing objects for a parcel, for back-to-front sorting. Counts come from the tile
 * total rather than from a width times a height, so an L-shaped wood gets the trees its
 * own area asks for and none of them stand in the notch.
 */
export function propsFor(p: IsoParcel, showSigns = true): Prop[] {
  const out: Prop[] = [];
  const b = unionBounds(p.tiles);
  if (!b.w) return out;
  const area = unionArea(p.tiles);
  const r = rnd(b.x * 41 + b.y * 7 + 3);
  const one = (inset: number): Point | undefined => scatter(p.tiles, r, 1, inset)[0];
  if (p.t === 'wood') {
    for (let i = 0; i < Math.ceil(area * 1.5); i++) {
      const at = one(0.3);
      if (at) out.push({ k: 'tree', x: at[0], y: at[1], s: TREE.woodSize, kind: 'wood' });
    }
  } else if (p.t === 'orchard' && p.hops) {
    /* D251: the idle world's hop yard: two poles to a tile, each with its bine. */
    for (const key of [...p.tiles].sort()) {
      const [tx, ty] = key.split(',').map(Number) as [number, number];
      if (Number.isNaN(tx) || Number.isNaN(ty)) continue;
      out.push({ k: 'hop', x: tx + 0.3, y: ty + 0.5 }, { k: 'hop', x: tx + 0.75, y: ty + 0.5 });
    }
  } else if (p.t === 'orchard' && p.vines) {
    /* The idle world's vineyard: two short rows of vines across each tile. */
    for (const key of [...p.tiles].sort()) {
      const [tx, ty] = key.split(',').map(Number) as [number, number];
      if (Number.isNaN(tx) || Number.isNaN(ty)) continue;
      for (const fy of [0.3, 0.72])
        for (const fx of [0.25, 0.5, 0.75]) out.push({ k: 'vine', x: tx + fx, y: ty + fy });
    }
  } else if (p.t === 'orchard' && p.glasshouse) {
    /* D300: tomatoes, one glasshouse frame to a tile. */
    for (const key of [...p.tiles].sort()) {
      const [tx, ty] = key.split(',').map(Number) as [number, number];
      if (Number.isNaN(tx) || Number.isNaN(ty)) continue;
      out.push({ k: 'glasshouse', x: tx + 0.5, y: ty + 0.5, tier: p.tier });
    }
  } else if (p.t === 'orchard') {
    /* One tree in the middle of each tile, which is the handoff's row-and-column grid
       wherever the parcel is a rectangle. */
    for (const key of [...p.tiles].sort()) {
      const tp = key.split(',').map(Number);
      const [tx, ty] = tp;
      if (tx === undefined || ty === undefined || Number.isNaN(tx) || Number.isNaN(ty)) continue;
      // D268: orchard trees grow fuller with every milestone, to half as big again.
      const s = TREE.orchardSize(p.tier);
      out.push({ k: 'tree', x: tx + 0.5, y: ty + 0.5, s, kind: 'orchard' });
    }
  } else if (p.t === 'pasture') {
    /* The fence follows the outline, one prop per side, so the far rail sorts behind the
       animals and the near rail in front. A rectangle gives the handoff's four edges. */
    const strokes = unionStrokes(p.tiles);
    /* Which sides are the far ones is a question about the screen, not about the world, so
       it is asked of the facing each edge presents at this turn (D176). */
    const isFar = (f: (typeof strokes)[number]) => {
      const sf = screenFacing(f.facing);
      return sf === 'n' || sf === 'w';
    };
    const far = strokes.filter(isFar);
    const near = strokes.filter((e) => !isFar(e));
    const anchor = (e: (typeof strokes)[number]): Point => {
      const a: Point = [e.x1, e.y1],
        c: Point = [e.x2, e.y2];
      const lo = depth(a[0], a[1]) <= depth(c[0], c[1]) ? a : c;
      const hi = lo === a ? c : a;
      if (isFar(e)) return lo;
      // The east rail sits a hair further off so it sorts ahead of the south one.
      return screenFacing(e.facing) === 'e' ? stepBack(hi[0], hi[1]) : hi;
    };
    const fenced = p.fence ? { style: p.fence } : {};
    for (const e of far) {
      const [ax, ay] = anchor(e);
      out.push({ k: 'fence', x: ax, y: ay, x1: e.x1, y1: e.y1, x2: e.x2, y2: e.y2, ...fenced });
    }
    for (let i = 0; i < (p.n || 0); i++) {
      const at = one(0.45);
      if (!at) continue;
      const a: Extract<Prop, { k: 'animal' }> = {
        k: 'animal',
        x: at[0],
        y: at[1],
        kind: p.animal ?? 'sheep',
      };
      // D268: the idle world's animals take a better breed with the milestones reached.
      if (p.tier !== undefined) a.breed = breedOf(p.tier, a.kind);
      out.push(a);
    }
    for (const e of near) {
      const [ax, ay] = anchor(e);
      out.push({ k: 'fence', x: ax, y: ay, x1: e.x1, y1: e.y1, x2: e.x2, y2: e.y2, ...fenced });
    }
  } else if (p.t === 'meadow' && p.ground) {
    /* D327: the monument ground's memorials, each standing where the ground lays it out. */
    for (const m of monumentGround(b, p.ground.n, p.ground.marks.length).pieces)
      out.push({ k: 'memorial', ...m });
  } else if (p.t === 'meadow' && p.mark) {
    /* D245, D259: a festival's mark stands in the middle of its ground. */
    const m: Extract<Prop, { k: 'mark' }> = {
      k: 'mark',
      x: b.x + b.w / 2,
      y: b.y + b.h / 2,
      kind: p.mark,
    };
    if (p.markNew) m.fresh = true;
    out.push(m);
  } else if (p.t === 'meadow' && p.crocus) {
    /* D251: saffron beds: crocus in rows, four clumps to a tile. */
    for (const key of [...p.tiles].sort()) {
      const [tx, ty] = key.split(',').map(Number) as [number, number];
      if (Number.isNaN(tx) || Number.isNaN(ty)) continue;
      for (const fy of [0.3, 0.72])
        for (const fx of [0.3, 0.72]) out.push({ k: 'crocus', x: tx + fx, y: ty + fy });
    }
  } else if (p.t === 'meadow' && p.hives) {
    /* The idle world's bees: hives in rows, front first, one per hive owned. */
    const tiles = [...p.tiles].sort();
    for (let i = 0; i < p.hives; i++) {
      const key = tiles[Math.floor(i / 2) % tiles.length];
      if (!key) break;
      const [tx, ty] = key.split(',').map(Number) as [number, number];
      out.push({ k: 'hive', x: tx + (i % 2 ? 0.7 : 0.3), y: ty + 0.5 });
    }
  } else if (p.t === 'meadow') {
    for (let i = 0; i < BALE.perMeadow; i++) {
      const at = one(0.4);
      if (at) out.push({ k: 'bale', x: at[0], y: at[1] });
    }
  } else if (p.t === 'waste') {
    for (let i = 0; i < 7; i++) {
      const at = one(0.3);
      if (at) out.push({ k: 'rock', x: at[0], y: at[1], s: 4 + r() * 3 });
    }
  } else if (p.t === 'pond') {
    const bridged = new Set(p.bridges ?? []);
    for (let i = 0; i < 10; i++) {
      if (r() > 0.45) {
        const at = one(0.15);
        // No reeds grow through a bridge deck.
        if (at && !bridged.has(`${Math.floor(at[0])},${Math.floor(at[1])}`))
          out.push({ k: 'reed', x: at[0], y: at[1] });
      }
    }
    /* D239: a bridge spans the water, so it runs across the way the water flows. Water on
       either side of it along x means the stream runs along x and the deck along y. */
    const own = new Set(p.tiles);
    for (const key of [...bridged].sort()) {
      const [tx, ty] = key.split(',').map(Number) as [number, number];
      const along =
        own.has(`${tx - 1},${ty}`) || own.has(`${tx + 1},${ty}`) ? ('y' as const) : ('x' as const);
      out.push({ k: 'bridge', x: tx + 0.5, y: ty + 0.5, tx, ty, along });
    }
    // D301: a trout now and then, just under the surface.
    for (let i = 0; i < Math.max(1, Math.round(area / 5)); i++) {
      const at = one(0.2);
      if (at) out.push({ k: 'fish', x: at[0], y: at[1] });
    }
  }
  plotExtras(p, b, r, out, showSigns);
  return out;
}

/** A footprint on the ground at this turn: `[x0, x1, y0, y1]`, in turned world units. */
type Box = [number, number, number, number];

/** What the draw order needs of a standing thing: a kind and an anchor, and maybe more. */
export interface Standing2D {
  k: string;
  x: number;
  y: number;
  w?: number;
  h?: number;
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
}

/** D273: the half-size of a festival mark's ground, centred on its anchor. */
const MARK_HALF = 1;

/**
 * D273: the ground a standing thing covers, at this turn. A building covers its rectangle,
 * a fence its edge, a festival mark its two-tile square; anything else stands on a point.
 */
export function footprint(s: Standing2D): Box {
  let pts: Point[];
  if (
    (s.k === 'build' || s.k === 'piece' || s.k === 'filler') &&
    s.w !== undefined &&
    s.h !== undefined
  )
    pts = [
      [s.x, s.y],
      [s.x + s.w, s.y + s.h],
    ];
  else if (s.k === 'memorial' && s.w !== undefined && s.h !== undefined)
    pts = [
      [s.x - s.w / 2, s.y - s.h / 2],
      [s.x + s.w / 2, s.y + s.h / 2],
    ];
  else if (s.k === 'fence' && s.x1 !== undefined && s.y1 !== undefined)
    pts = [
      [s.x1, s.y1],
      [s.x2 ?? s.x1, s.y2 ?? s.y1],
    ];
  else if (s.k === 'mark')
    pts = [
      [s.x - MARK_HALF, s.y - MARK_HALF],
      [s.x + MARK_HALF, s.y + MARK_HALF],
    ];
  else pts = [[s.x, s.y]];
  const t = pts.map(([x, y]) => turnWorld(x, y));
  const xs = t.map((q) => q[0]),
    ys = t.map((q) => q[1]);
  return [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
}

/**
 * D273: whether `a` is drawn before `b`. At this turn the eye looks down both turned axes,
 * so one footprint is behind another when it ends before the other starts along an axis on
 * which the two overlap, or along both. Two things with no such relation do not overlap on
 * screen in a way the order can change, and are left to `depth`.
 */
export function isBehind(a: Box, b: Box): boolean {
  const xBefore = a[1] <= b[0] && !(b[1] <= a[0]);
  const yBefore = a[3] <= b[2] && !(b[3] <= a[2]);
  /* Strict: two things that only touch, a fence's corner and the next fence, or a hen on
     the line of a wall, share no ground, and counting them as overlapping makes a loop. */
  const xOverlap = a[0] < b[1] && b[0] < a[1];
  const yOverlap = a[2] < b[3] && b[2] < a[3];
  return (xBefore && (yOverlap || yBefore)) || (yBefore && xOverlap);
}

/**
 * D273: everything standing on the world, back to front, so nothing behind a building is
 * drawn in front of it: a hen behind the coop is under its roof, not on it. The order is
 * the footprints' own (`isBehind`), with `depth` choosing among things free to go next. A
 * tangle, which only overlapping footprints can make, falls back to `depth`.
 */
export function sortStanding<T extends Standing2D>(items: T[]): T[] {
  const n = items.length;
  const box = items.map(footprint);
  const key = items.map((s) => depth(s.x, s.y));
  const before: number[][] = items.map(() => []);
  const need = new Array<number>(n).fill(0);
  for (let i = 0; i < n; i++)
    for (let j = 0; j < n; j++)
      if (i !== j && isBehind(box[i] as Box, box[j] as Box)) {
        (before[i] as number[]).push(j);
        need[j] = (need[j] as number) + 1;
      }
  const done = new Array<boolean>(n).fill(false);
  const out: T[] = [];
  for (let step = 0; step < n; step++) {
    let pick = -1;
    for (let i = 0; i < n; i++) {
      if (done[i] || need[i]) continue;
      if (pick < 0 || (key[i] as number) < (key[pick] as number)) pick = i;
    }
    if (pick < 0)
      for (let i = 0; i < n; i++)
        if (!done[i] && (pick < 0 || (key[i] as number) < (key[pick] as number))) pick = i;
    done[pick] = true;
    out.push(items[pick] as T);
    for (const j of before[pick] as number[]) need[j] = (need[j] as number) - 1;
  }
  return out;
}

/**
 * A point a little in from one corner of a parcel's bounds, kept on the parcel. `k` counts
 * round from the corner nearest the eye at this turn, so the tier sign stands at the front
 * whichever way the world is turned.
 */
function plotCorner(p: IsoParcel, b: Bounds, k: number, inset: number): Point | undefined {
  const corners: Point[] = [
    [b.x, b.y],
    [b.x + b.w, b.y],
    [b.x + b.w, b.y + b.h],
    [b.x, b.y + b.h],
  ];
  const c = corners[(frontCorner(b.x, b.y, b.w, b.h) + k) % 4] as Point;
  const cx = b.x + b.w / 2,
    cy = b.y + b.h / 2;
  const at: Point = [c[0] + Math.sign(cx - c[0]) * inset, c[1] + Math.sign(cy - c[1]) * inset];
  return inUnion(p.tiles, at[0], at[1]) ? at : undefined;
}

/** D268, D269, D305: what any idle plot carries on top of its own drawing. */
function plotExtras(
  p: IsoParcel,
  b: Bounds,
  r: () => number,
  out: Prop[],
  showSigns: boolean,
): void {
  if (p.sign && showSigns) {
    const at = plotCorner(p, b, 0, 0.32) ?? scatter(p.tiles, r, 1, 0.3)[0];
    if (at) out.push({ k: 'sign', x: at[0], y: at[1], text: p.sign });
  }
  if (p.hand) {
    const at = plotCorner(p, b, 3, 0.75) ?? scatter(p.tiles, r, 1, 0.4)[0];
    if (at)
      out.push({ k: 'worker', x: at[0], y: at[1], ...(p.workers ? { look: p.workers } : {}) });
  }
  // D329: more hands about the plot as its milestones mount.
  const more = Math.min(
    DENSITY.maxWorkers - (p.hand ? 1 : 0),
    Math.floor((p.tier ?? 0) / DENSITY.tiersPerWorker),
  );
  for (let i = 0; i < more; i++) {
    const at = scatter(p.tiles, r, 1, 0.4)[0];
    if (at)
      out.push({ k: 'worker', x: at[0], y: at[1], ...(p.workers ? { look: p.workers } : {}) });
  }
  // D353: chaff over a worked field while its workers harvest it (`atCycle` sets `harvest`).
  if (p.harvest) {
    const pts = scatter(
      p.tiles,
      rnd(b.x * 7 + b.y * 3 + 11),
      Math.min(14, 2 + p.tiles.length),
      0.2,
    );
    out.push({ k: 'busy', x: b.x + b.w / 2, y: b.y + b.h / 2, pts });
  }
  if (p.burst)
    out.push({ k: 'burst', x: b.x + b.w / 2, y: b.y + b.h / 2, r: Math.max(b.w, b.h) / 2 });
  // D345: no hand, and the bar is full: a small icon of the crop bobs over the field.
  if (p.ready && p.id)
    out.push({
      k: 'ready',
      x: b.x + b.w / 2,
      y: b.y + b.h / 2,
      id: p.id,
      icon: `producer_${p.id}`,
    });
}

/* ---------- machines at work ----------
   A field with a job running shows the machine doing it. The job vocabulary is the sim's
   own (packages/data/content/machines.json). Every job but the two harvests is a power
   unit pulling an implement; harvesters are self-propelled and carry a header. The power
   unit takes the maker's colour, the only place a maker shows on the map. */

export type Rig =
  'plough' | 'drill' | 'spreader' | 'tanker' | 'sprayer' | 'mower' | 'combine' | 'rootharvester';

export type WorkedGround = 'ploughed' | 'tint' | 'cut' | 'stubble' | 'bare';

export interface JobRig {
  rig: Rig;
  worked: WorkedGround;
}

export const JOB_RIG: Record<string, JobRig> = {
  plough: { rig: 'plough', worked: 'ploughed' },
  sow: { rig: 'drill', worked: 'ploughed' },
  sow_roots: { rig: 'drill', worked: 'ploughed' },
  fertilise: { rig: 'spreader', worked: 'tint' },
  spray: { rig: 'sprayer', worked: 'tint' },
  mow: { rig: 'mower', worked: 'cut' },
  ted: { rig: 'mower', worked: 'cut' },
  rake: { rig: 'mower', worked: 'cut' },
  harvest_grain: { rig: 'combine', worked: 'stubble' },
  harvest_roots: { rig: 'rootharvester', worked: 'bare' },
};

function workedFill(kind: WorkedGround, fill: string, P: Palette): string {
  if (kind === 'ploughed') return mix(P.soil, '#000000', 0.1);
  if (kind === 'bare') return P.soil;
  if (kind === 'stubble') return mix(fill, '#C9A227', 0.35);
  if (kind === 'cut') return mix(fill, '#000000', 0.12);
  return mix(fill, '#000000', 0.07);
}

function wheel(cx: number, cy: number, r: number, o: string[]) {
  o.push(
    `<ellipse cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" rx="${r.toFixed(1)}" ry="${(r * 0.66).toFixed(1)}" fill="#2E2A24"/>`,
  );
  o.push(
    `<ellipse cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" rx="${(r * 0.42).toFixed(1)}" ry="${(r * 0.28).toFixed(1)}" fill="#8A857C"/>`,
  );
}

/** Tractor: big rear wheels, small front, bonnet, glazed cab, exhaust. */
function powerUnit(
  wx: number,
  wy: number,
  maker: string | undefined,
  P: Palette,
  o: string[],
  s = 1.05,
) {
  const body = (maker && P.maker[maker as keyof typeof P.maker]) || P.maker.ashcombe;
  const [cx, cy] = project(wx, wy);
  o.push(
    `<ellipse cx="${cx.toFixed(1)}" cy="${(cy + 2).toFixed(1)}" rx="${(14 * s).toFixed(1)}" ry="${(5.4 * s).toFixed(1)}" fill="rgba(42,36,24,.26)"/>`,
  );
  wheel(cx - 6.4 * s, cy + 1.6 * s, 3.1 * s, o);
  wheel(cx - 2.6 * s, cy + 3.4 * s, 3.1 * s, o);
  wheel(cx + 4.4 * s, cy - 1.2 * s, 4.4 * s, o);
  wheel(cx + 8.2 * s, cy + 0.8 * s, 4.4 * s, o);
  isoBox(
    wx - 0.15,
    wy - 0.13,
    0.3,
    0.26,
    8 * s,
    tint(body, 0.18),
    shade(body, 0.28),
    shade(body, 0.1),
    o,
  );
  isoBox(
    wx + 0.08,
    wy + 0.05,
    0.24,
    0.22,
    14 * s,
    tint(body, 0.06),
    shade(body, 0.32),
    shade(body, 0.14),
    o,
  );
  const g = project(wx + 0.14, wy + 0.11, 14 * s);
  o.push(
    `<rect x="${(g[0] - 4.4 * s).toFixed(1)}" y="${(g[1] - 1.2 * s).toFixed(1)}" width="${(8.8 * s).toFixed(1)}" height="${(5 * s).toFixed(1)}" rx="1.4" fill="rgba(198,219,228,.72)"/>`,
  );
  o.push(
    `<rect x="${(g[0] - 4.4 * s).toFixed(1)}" y="${(g[1] - 1.2 * s).toFixed(1)}" width="${(3 * s).toFixed(1)}" height="${(5 * s).toFixed(1)}" rx="1.4" fill="rgba(255,255,255,.28)"/>`,
  );
  o.push(
    `<rect x="${(cx - 4.5 * s).toFixed(1)}" y="${(cy - 19 * s).toFixed(1)}" width="${(1.7 * s).toFixed(1)}" height="${(6 * s).toFixed(1)}" rx=".8" fill="${shade(body, 0.5)}"/>`,
  );
}

function implement(wx: number, wy: number, kind: Rig, P: Palette, o: string[]) {
  const [cx, cy] = project(wx, wy);
  const STEEL = P.steel,
    dark = shade(P.steel, 0.45);
  o.push(
    `<ellipse cx="${cx.toFixed(1)}" cy="${(cy + 1.5).toFixed(1)}" rx="11" ry="4" fill="rgba(42,36,24,.20)"/>`,
  );
  if (kind === 'plough') {
    for (let i = 0; i < 4; i++) {
      const x = cx - 7 + i * 4.6,
        y = cy - 2 + i * 1.8;
      o.push(
        `<path d="M${x.toFixed(1)} ${(y - 6).toFixed(1)} l3.4 2.2 l0 5.6 l-3.4 -2.2 z" fill="${STEEL}"/>`,
      );
      o.push(
        `<path d="M${x.toFixed(1)} ${y.toFixed(1)} l3.4 2.2" stroke="${dark}" stroke-width="1.8"/>`,
      );
    }
  } else if (kind === 'drill') {
    isoBox(
      wx - 0.18,
      wy - 0.14,
      0.3,
      0.27,
      10,
      tint(STEEL, 0.24),
      shade(STEEL, 0.3),
      shade(STEEL, 0.12),
      o,
    );
    for (let i = 0; i < 5; i++)
      o.push(
        `<rect x="${(cx - 9 + i * 3.8).toFixed(1)}" y="${(cy - 1 + i * 1).toFixed(1)}" width="1.5" height="5" fill="${dark}"/>`,
      );
  } else if (kind === 'spreader' || kind === 'tanker') {
    const hop = kind === 'tanker' ? '#93A7B2' : '#9A8B70';
    isoBox(
      wx - 0.2,
      wy - 0.16,
      0.34,
      0.3,
      13,
      tint(hop, 0.2),
      shade(hop, 0.3),
      shade(hop, 0.12),
      o,
    );
    if (kind === 'spreader')
      o.push(
        `<ellipse cx="${(cx - 9).toFixed(1)}" cy="${(cy + 2).toFixed(1)}" rx="6" ry="2.4" fill="rgba(206,186,126,.5)"/>`,
      );
  } else if (kind === 'sprayer') {
    isoBox(wx - 0.18, wy - 0.14, 0.28, 0.25, 11, '#D3DCDE', '#8E999C', '#AEB9BC', o);
    o.push(
      `<path d="M${(cx - 17).toFixed(1)} ${(cy + 4).toFixed(1)} L${(cx + 13).toFixed(1)} ${(cy - 6).toFixed(1)}" stroke="${STEEL}" stroke-width="1.8"/>`,
    );
  } else if (kind === 'mower') {
    o.push(
      `<path d="M${(cx - 13).toFixed(1)} ${(cy + 2).toFixed(1)} L${(cx + 8).toFixed(1)} ${(cy - 5).toFixed(1)} l1.8 3.4 l-21 7 z" fill="${STEEL}"/>`,
    );
  }
}

function harvester(
  wx: number,
  wy: number,
  kind: Rig,
  maker: string | undefined,
  P: Palette,
  o: string[],
) {
  const STEEL = P.steel;
  const body = (maker && P.maker[maker as keyof typeof P.maker]) || P.maker.ashcombe;
  const [cx, cy] = project(wx, wy);
  o.push(
    `<ellipse cx="${cx.toFixed(1)}" cy="${(cy + 2.5).toFixed(1)}" rx="19" ry="7.5" fill="rgba(42,36,24,.26)"/>`,
  );
  wheel(cx - 6, cy + 2.4, 4.4, o);
  wheel(cx - 2, cy + 4.4, 4.4, o);
  wheel(cx + 8, cy - 1.6, 3, o);
  isoBox(
    wx - 0.16,
    wy - 0.16,
    0.46,
    0.38,
    15,
    tint(body, 0.14),
    shade(body, 0.3),
    shade(body, 0.12),
    o,
  );
  isoBox(
    wx + 0.13,
    wy + 0.04,
    0.2,
    0.2,
    24,
    tint(body, 0.04),
    shade(body, 0.34),
    shade(body, 0.16),
    o,
  );
  const g = project(wx + 0.17, wy + 0.09, 24);
  o.push(
    `<rect x="${(g[0] - 5.4).toFixed(1)}" y="${(g[1] - 1.4).toFixed(1)}" width="10.8" height="6" rx="1.4" fill="rgba(198,219,228,.72)"/>`,
  );
  const h = project(wx + 0.52, wy + 0.38);
  if (kind === 'combine') {
    o.push(
      `<path d="M${(h[0] - 18).toFixed(1)} ${(h[1] - 1).toFixed(1)} L${(h[0] + 11).toFixed(1)} ${(h[1] - 15).toFixed(1)} l3.4 6 l-29 14 z" fill="${STEEL}"/>`,
    );
    o.push(
      `<path d="M${(h[0] - 17).toFixed(1)} ${(h[1] - 5).toFixed(1)} L${(h[0] + 11).toFixed(1)} ${(h[1] - 19).toFixed(1)}" stroke="#9A9A93" stroke-width="2.8"/>`,
    );
    for (let i = 0; i < 6; i++) {
      const t = i / 5,
        px = h[0] - 17 + t * 28,
        py = h[1] - 5 - t * 14;
      o.push(
        `<path d="M${px.toFixed(1)} ${py.toFixed(1)} l0 4" stroke="#C9C9C0" stroke-width="1.1"/>`,
      );
    }
  } else {
    o.push(
      `<path d="M${(h[0] - 13).toFixed(1)} ${(h[1] - 1).toFixed(1)} L${(h[0] + 7).toFixed(1)} ${(h[1] - 11).toFixed(1)} l2.4 5 l-20 10 z" fill="${STEEL}"/>`,
    );
  }
  o.push(
    `<path d="M${(cx - 3).toFixed(1)} ${(cy - 20).toFixed(1)} l-13 -3 l0 3.4 l13 3 z" fill="${shade(body, 0.18)}"/>`,
  );
}

/** Where a machine stands, what it is, and the run it travels (D164). */
export interface MachineAt {
  x: number;
  y: number;
  spec: JobRig;
  maker?: string;
  /** The line down the field this one works, from `machineRun`. */
  run: MachineRun;
}

/**
 * How long a machine takes to cross a tile, in seconds. A job runs for hours, so the walk
 * down the field is far too slow to see; what the eye reads as work is the machine going
 * up and down the run, and this is the pace it goes at. Slow enough to be a tractor in a
 * field rather than something darting about.
 */
export const MACHINE_SECS_PER_TILE = 3.4;

/** Seconds for one there-and-back along a run, floored so a one-tile field is not frantic. */
export const runSeconds = (len: number): number =>
  Math.max(9, Math.round(len * 2 * MACHINE_SECS_PER_TILE * 10) / 10);

/**
 * The machines working a field, and the ground they have already covered. The worked band
 * grows from the near edge with progress, so a field mid-job reads at a glance: part
 * turned, part standing, and the machines on the line between them. The band is the union
 * clipped at that line, not a rectangle, so it stops where the field stops.
 *
 * D154: one machine per crew on the job, each with its own lane on the working line.
 */
export function paintFieldJob(p: IsoParcel, P: Palette, o: string[]): MachineAt[] {
  const job = p.job;
  if (!job || !job.kind) return [];
  const spec = JOB_RIG[job.kind];
  if (!spec) return [];
  const b = unionBounds(p.tiles);
  if (!b.w) return [];
  const t = Math.max(0, Math.min(1, job.progress ?? 0));
  const fill = parcelFill(p, P);
  const cutY = b.y + b.h * t;
  if (t > 0.01) {
    const done = workedFill(spec.worked, fill, P);
    o.push(`<path d="${unionPath(p.tiles, 0, cutY)}" fill="${done}" fill-rule="evenodd"/>`);
    const lines: string[] = [];
    for (let i = 0.3; i < b.h * t; i += 0.3) lines.push(...rowLines(p.tiles, b.y + i));
    o.push(`<g stroke="${alpha(shade(done, 0.5), 0.34)}" stroke-width="1.2">${lines.join('')}</g>`);
  }
  const makers = job.makers?.length ? job.makers : [job.maker];
  const out: MachineAt[] = [];
  makers.forEach((maker, i) => {
    const run = machineRun(p.tiles, t, i, makers.length);
    if (!run) return;
    out.push({ x: run.at[0], y: run.at[1], spec, ...(maker ? { maker } : {}), run });
  });
  return out;
}

/**
 * Draws the machine itself. Separate from the ground so it sorts with the other props, and
 * drawn at (0, 0) by the map, which puts it where the run says with one transform and
 * leaves the travel to a second one (D164).
 */
/**
 * A machine from above (D178): the rig on its lane, its implement across the run behind it
 * or its header across the run in front. Drawn as world rectangles rather than as screen
 * shapes, so it lies along the lane it is working at every turn.
 */
function planMachine(
  wx: number,
  wy: number,
  rig: Rig,
  maker: string | undefined,
  P: Palette,
  o: string[],
): void {
  const body = (maker && P.maker[maker as keyof typeof P.maker]) || P.maker.ashcombe;
  const harvest = rig === 'combine' || rig === 'rootharvester';
  const s = harvest ? 1.3 : 1;
  const rect = (dx: number, dy: number, w: number, h: number, fill: string) =>
    o.push(
      `<path d="${polyPath(rectPts(wx + dx * s, wy + dy * s, w * s, h * s))}" fill="${fill}"/>`,
    );
  if (harvest) rect(0.2, -0.34, 0.13, 0.68, P.steel);
  else if (rig !== 'plough') rect(-0.62, -0.28, 0.18, 0.56, P.steel);
  else rect(-0.58, -0.22, 0.22, 0.44, P.steel);
  rect(-0.22, -0.21, 0.44, 0.07, '#2E2A24');
  rect(-0.22, 0.14, 0.44, 0.07, '#2E2A24');
  rect(-0.2, -0.15, 0.42, 0.3, body);
  rect(-0.02, -0.08, 0.14, 0.16, 'rgba(198,219,228,.72)');
}

export function paintMachine(
  wx: number,
  wy: number,
  rig: Rig,
  maker: string | undefined,
  P: Palette,
  o: string[],
): void {
  if (isPlan()) {
    planMachine(wx, wy, rig, maker, P, o);
    return;
  }
  if (rig === 'combine' || rig === 'rootharvester') {
    harvester(wx, wy, rig, maker, P, o);
    return;
  }
  implement(wx - 0.38, wy - 0.3, rig, P, o);
  powerUnit(wx, wy, maker, P, o);
}

/* ---------- ambient motion (D164) ----------
   Decoration, carrying no state at all: the smoke over the yard and the water moving. Both
   come back as numbers rather than as markup, because the map paints them as real elements
   rather than into its one innerHTML string. That string is rebuilt on every clock tick,
   and anything animated inside it would start again from the top once a second. */

/** One puff of smoke, in screen space, with the seconds it waits before it rises. */
export interface Puff {
  cx: number;
  cy: number;
  r: number;
  delay: number;
}

/** Puffs of smoke over a world building, starting at the ridge of its roof. */
export function smokePuffs(b: {
  x: number;
  y: number;
  w: number;
  h: number;
  kind?: BuildingKind;
}): Puff[] {
  /* D199: the farmhouse's smoke leaves its own chimney. */
  const ch = b.kind === 'house' ? houseChimney(b.x, b.y, b.w, b.h) : null;
  const [cx, cy] = ch
    ? project(ch.x, ch.y, ch.top + 6)
    : project(b.x + b.w * 0.3, b.y + b.h * 0.3, 52);
  return [0, 3.1, 6.2].map((delay, i) => ({
    cx: cx + i * 0.8,
    cy,
    r: SMOKE.r + i * SMOKE.grow,
    delay,
  }));
}

/** One line of light on water, in screen space, with the seconds it waits before it drifts. */
export interface Ripple {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  delay: number;
}

/** A couple of lines of light across a pond, inset so neither touches the bank. */
export function pondRipples(tiles: Iterable<TileKey>): Ripple[] {
  const b = unionBounds(tiles);
  if (!b.w) return [];
  const out: Ripple[] = [];
  [0.36, 0.66].forEach((f, i) => {
    const y = b.y + b.h * f;
    let best: [number, number] | undefined;
    for (const sp of rowSpans(tiles, Math.min(y, b.y + b.h - 1e-6)))
      if (!best || sp[1] - sp[0] > best[1] - best[0]) best = sp;
    // A pond narrower than this has no room for a line that reads as water rather than
    // as a mark on it, so it gets none.
    if (!best || best[1] - best[0] < 1.2) return;
    const [ax, ay] = project(best[0] + 0.25, y),
      [bx, by] = project(best[1] - 0.25, y);
    out.push({ x1: ax, y1: ay, x2: bx, y2: by, delay: i * 2.4 });
  });
  return out;
}

/* ---------- overlays, the ghost and the rings (D150) ----------
   The flat map draws these and the isometric port shipped without them, which would make
   the swap a regression. Each is union-based, as D149 asks: a field is any shape, so
   every outline here follows `unionPath` and never a bounding box.

   They come in two layers, because the map is lit. The washes go down with the land and
   take the hour's cast and the weather with them; the marks go over the cast, so the reach
   line, the power flag, the placement ghost and the guide ring stay crisp at dusk in a
   storm, which is what TileGrid does with the same marks under D144. */

/** The overlays the Key offers, spelled as `OverlayToggle` spells them. */
export type IsoOverlay = 'none' | 'soil' | 'power' | 'reach';

/** A building the power overlay can flag: the shape `isoBuildingsOf` returns. */
export interface IsoBuildingMark {
  x: number;
  y: number;
  w: number;
  h: number;
  powered?: boolean;
}

export interface IsoOverlayInput {
  overlay?: IsoOverlay;
  parcels?: IsoParcel[];
  buildings?: IsoBuildingMark[];
  /** Soil by tile key, as `soilMap` returns it. A field takes the reading of its tiles. */
  soil?: Record<string, number>;
  /** D116: the tiles the world's best tractor can reach. */
  reach?: Iterable<TileKey>;
}

/* Tokens rather than hex: these are system colours, not map materials, so they follow the
   theme with the rest of the interface. The fallback is for a host with no stylesheet,
   which is the handoff's standalone demo. */
const ACCENT = 'var(--accent, #3d6b45)';
const AMBER = 'var(--amber, #c98a1b)';
const INK = 'var(--ink, #1e221e)';
const SURFACE = 'var(--surface, #ffffff)';

/** Every tile the world holds: the parcels, plus the ground its buildings stand on. */
export function ownedTiles(
  parcels: IsoParcel[] = [],
  buildings: IsoBuildingMark[] = [],
): TileKey[] {
  const out = new Set<TileKey>();
  for (const p of parcels) for (const k of p.tiles) out.add(k);
  for (const b of buildings)
    for (let dy = 0; dy < Math.max(1, b.h); dy++)
      for (let dx = 0; dx < Math.max(1, b.w); dx++) out.add(`${b.x + dx},${b.y + dy}`);
  return [...out];
}

/**
 * The washes an overlay lays over the land: the soil reading on each field, and the tint
 * on everything the best tractor cannot reach. Both sit under the hour's cast, so they
 * darken with the board rather than floating over it.
 */
export function paintOverlayWash(input: IsoOverlayInput, P: Palette, o: string[]): void {
  const { overlay = 'none', parcels = [], buildings = [], soil = {}, reach } = input;
  if (overlay === 'soil') {
    for (const p of parcels) {
      if (p.t !== 'field' || !p.tiles.length) continue;
      let reading: number | undefined;
      for (const k of p.tiles) {
        const v = soil[k];
        if (v !== undefined) {
          reading = v;
          break;
        }
      }
      const op = soilOpacity(reading);
      if (op <= 0) continue;
      o.push(
        `<path data-testid="iso-soil" data-tile="${p.tiles[0] ?? ''}" d="${unionPath(p.tiles)}" fill="${P.soil}" fill-rule="evenodd" opacity="${op.toFixed(3)}" style="mix-blend-mode:multiply"/>`,
      );
    }
    return;
  }
  if (overlay === 'reach' && reach) {
    /* D116: the tint is worked out from the set, not from a flag on each tile, and it is
       drawn as one shape so no seam shows between two tiles that are both out of reach. */
    const set = new Set(reach);
    const outside = ownedTiles(parcels, buildings).filter((k) => !set.has(k));
    if (!outside.length) return;
    o.push(
      `<path data-testid="iso-out-of-reach" d="${unionPath(outside)}" fill="${INK}" fill-rule="evenodd" opacity="0.18"/>`,
    );
  }
}

/**
 * The marks an overlay puts over the lit board: the one line round the reach (D116), and
 * a flag on each unpowered building (D60). D150 settles the second: a dashed square round
 * an isometric polygon reads as a shape on the ground rather than as a state of the
 * building, so the power overlay marks the building itself.
 */
export function paintOverlayMarks(input: IsoOverlayInput, P: Palette, o: string[]): void {
  const { overlay = 'none', buildings = [], reach } = input;
  if (overlay === 'reach' && reach) {
    const keys = [...reach];
    if (!keys.length) return;
    // One path for the whole boundary, however many sides the reach has. Never per tile.
    o.push(
      `<path data-testid="iso-reach-edge" d="${unionPath(keys)}" fill="none" stroke="${ACCENT}" stroke-width="2.5" stroke-linejoin="round"/>`,
    );
    return;
  }
  if (overlay === 'power') {
    for (const b of buildings) {
      if (b.powered !== false) continue;
      paintPowerMark(b, P, o);
    }
  }
}

/** How far above a building's footprint the power flag stands, clear of the tallest roof. */
export const POWER_MARK_LIFT = 58;

/**
 * The flag itself: a disc over the building's own ground, with a bolt in it. A disc, not a
 * dashed outline, because it has to read at a glance on a polygon of any size, and it is
 * drawn over the light so a storm at dusk cannot swallow it.
 */
export function paintPowerMark(b: IsoBuildingMark, P: Palette, o: string[]): void {
  const [cx, cy] = project(b.x + Math.max(1, b.w) / 2, b.y + Math.max(1, b.h) / 2);
  /* D178: the flag stands clear of the roof in the isometric view. From above the roof has
     no height for it to stand clear of, so it sits on the building itself. */
  const my = isPlan() ? cy : cy - POWER_MARK_LIFT;
  const bolt: Array<[number, number]> = [
    [1.6, -5],
    [-3, 0.6],
    [-0.4, 0.6],
    [-1.6, 5],
    [3, -0.6],
    [0.4, -0.6],
  ];
  o.push(
    `<g data-testid="iso-power-mark" data-tile="${b.x},${b.y}">` +
      (isPlan()
        ? ''
        : `<path d="M${cx.toFixed(1)} ${(my + 9).toFixed(1)} L${cx.toFixed(1)} ${cy.toFixed(1)}" stroke="${alpha(shade(P.steel, 0.4), 0.45)}" stroke-width="1.5"/>`) +
      `<circle cx="${cx.toFixed(1)}" cy="${my.toFixed(1)}" r="9" fill="${SURFACE}" stroke="${AMBER}" stroke-width="2.5"/>` +
      `<path d="${polyPath(bolt.map(([dx, dy]) => [cx + dx, my + dy] as Point))}" fill="${AMBER}"/>` +
      `</g>`,
  );
}

/**
 * D54 and D136: the footprint a placement would take, under the player's finger before
 * they confirm. A filled accent body so it reads as "these, if you confirm", the union's
 * own outline round the lot, and a thin line per tile so the player can see which tiles
 * were picked — the same three parts the flat map's `highlight` draws, and distinct from
 * the selection ring, which is ink and white, and from the guide ring, which is dashed.
 */
export function paintGhost(tiles: TileKey[], o: string[]): void {
  if (!tiles.length) return;
  o.push(
    `<path data-testid="iso-ghost-edge" d="${unionPath(tiles)}" fill="${ACCENT}" fill-opacity="0.18" fill-rule="evenodd" stroke="${ACCENT}" stroke-width="2.5" stroke-linejoin="round"/>`,
  );
  for (const key of tiles) {
    const p = parseTileKey(key);
    if (!p) continue;
    o.push(
      `<path data-testid="iso-ghost-tile-${key}" d="${polyPath(rectPts(p[0], p[1], 1, 1))}" fill="none" stroke="${ACCENT}" stroke-width="1" opacity="0.55"/>`,
    );
  }
}

/**
 * D149's selection ring, lifted out of the map so it can be drawn over the light with the
 * rest of the marks. Ink with a white line inside it, which is nothing else on the board.
 */
export function paintSelection(tiles: TileKey[], o: string[]): void {
  if (!tiles.length) return;
  const d = unionPath(tiles);
  o.push(
    `<path data-testid="iso-select-ring" d="${d}" fill="none" stroke="#1E221E" stroke-width="2.5"/>`,
  );
  o.push(`<path d="${d}" fill="none" stroke="#ffffff" stroke-width="1" opacity=".6"/>`);
}

/**
 * D44's guide ring, and D87's pulse. The dash and the pulse are the walkthrough's own CSS
 * classes, so nothing here animates, and the halo underneath keeps the dash readable on a
 * pale field, exactly as on the flat map.
 */
export function paintGuide(tiles: TileKey[], pulse: number, o: string[]): void {
  if (!tiles.length) return;
  const d = unionPath(tiles);
  o.push(`<path class="walk-ring-halo" d="${d}" fill="none" stroke="${INK}" stroke-width="5"/>`);
  o.push(
    `<path data-testid="walk-ring" class="${pulse > 0 ? 'walk-ring walk-ring-pulse' : 'walk-ring'}" d="${d}" fill="none" stroke="${ACCENT}" stroke-width="2"/>`,
  );
}

/* ---------- D437: the lanes and the paving between plots ---------- */

/**
 * D437: how a theme's lane looks at its track surface (D287), the same in both maps: its
 * colour, its width in tiles, the bridge's timber where it crosses water, and the dashes down
 * the middle a theme paints there (the module's `lane()` dressing), if any.
 */
export function laneLook(
  theme: string | undefined,
  surface: string,
): { colour: string; width: number; bridge: string; dash?: string } {
  switch (theme) {
    case 'compute':
      return { colour: '#55585E', width: 0.5, bridge: '#55585E', dash: '#F2EFE6' };
    case 'motorsport':
      return {
        colour: surface === 'gravel' ? MOTOR.gravel : MOTOR.tarmac,
        width: 0.5,
        bridge: MOTOR.tarmac,
      };
    case 'arena':
      return {
        colour: ROADS[surface] ?? (ROADS.dirt_road as string),
        width: 0.46,
        bridge: TRACK.bridge,
      };
    default:
      return {
        colour: TRACK_SURFACE[surface] ?? (TRACK_SURFACE.dirt as string),
        width: TRACK.width,
        bridge: TRACK.bridge,
      };
  }
}

/** D437: a strip `w` wide from corner `a` to corner `b`, flat on the ground. */
function laneStrip(
  a: { x: number; y: number },
  b: { x: number; y: number },
  w: number,
  fill: string,
  o: string[],
): void {
  const dx = b.x - a.x,
    dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const nx = (-dy / len) * (w / 2),
    ny = (dx / len) * (w / 2);
  // Out past each end by half its width, so the runs meet at their corners.
  const ex = (dx / len) * (w / 2),
    ey = (dy / len) * (w / 2);
  o.push(
    `<path d="${polyPath([
      project(a.x - ex + nx, a.y - ey + ny),
      project(b.x + ex + nx, b.y + ey + ny),
      project(b.x + ex - nx, b.y + ey - ny),
      project(a.x - ex - nx, a.y - ey - ny),
    ])}" fill="${fill}"/>`,
  );
}

/**
 * D437: the open track as the theme's lanes, as the 3D world lays them: each straight run in
 * the theme's surface, the dashes down the middle where the theme has them, and the bridge's
 * timber where the track crosses water.
 */
export function paintLanes(
  track: {
    surface: string;
    edges: ReadonlyArray<{
      from: { x: number; y: number };
      to: { x: number; y: number };
      wet: boolean;
    }>;
  },
  theme: string | undefined,
  o: string[],
): void {
  const look = laneLook(theme, track.surface);
  for (const e of track.edges)
    if (e.wet) laneStrip(e.from, e.to, look.width + 0.14, look.bridge, o);
  for (const r of laneRuns(track.edges)) {
    const a = { x: r.x, y: r.y },
      b = r.along === 'x' ? { x: r.x + r.len, y: r.y } : { x: r.x, y: r.y + r.len };
    laneStrip(a, b, look.width, look.colour, o);
    if (!look.dash) continue;
    // As the module's `lane()` lays them: half a tile long, one every 1.2 tiles.
    for (let i = 0; i < r.len / 1.2; i++) {
      const d = 0.35 + i * 1.2;
      const p = r.along === 'x' ? { x: r.x + d, y: r.y } : { x: r.x, y: r.y + d };
      const q = r.along === 'x' ? { x: p.x + 0.5, y: p.y } : { x: p.x, y: p.y + 0.5 };
      laneStrip(p, q, 0.05, look.dash, o);
    }
  }
}

/** D437: the gaps between neighbouring plots closed into the theme's paving, the yard's own. */
export function paintPaving(tiles: readonly TileKey[], P: Palette, o: string[]): void {
  if (!tiles.length) return;
  o.push(`<path d="${unionPath(tiles)}" fill="${P.yard}"/>`);
}

/* ---------- D440: the one-tile fillers on lone tiles ---------- */

/** D440: a filler standing on a lone tile (`worldFillers`), for the standing sort. */
export interface FillerProp {
  k: 'filler';
  x: number;
  y: number;
  variant: number;
}

/** A world unit's height in the flat map's pixels, as the kits stand things. */
const FZ = (TW / 2 / Math.SQRT1_2) * Math.cos(Math.PI / 6);

/**
 * D440: the asset module's four fillers per theme (`filler`), drawn simply for the flat map:
 * farm a pond with reeds, a bale and cart, a hive with flowers, a small copse; compute a
 * generator, a bike shelter, a planter and bench, a water tank; motorsport a tyre wall, a small
 * stand, a sponsor board, a parked car; arena a well, a statue, a campfire, a practice dummy.
 * In the theme's map colours (`THEME_MAP_COLOURS`), and under winter's snow.
 */
export function paintFiller(theme: string, f: FillerProp, P: Palette, o: string[]): void {
  const t = (theme in THEME_MAP_COLOURS ? theme : 'farm') as keyof typeof THEME_MAP_COLOURS;
  const A: Record<string, string> = { ...ASSET_PALETTE, ...THEME_MAP_COLOURS[t] };
  const c = (k: string): string => A[k] ?? k;
  const snow = P.snow ?? 0;
  const cover = (col: string): string => mix(col, SNOW.colour, 0.8 * snow);
  const { x, y } = f;
  const v = ((f.variant % 4) + 4) % 4;
  // A box at (dx, dy) in the tile, `w` by `d`, `h` units tall, its top under any snow.
  const box = (
    dx: number,
    dy: number,
    w: number,
    d: number,
    h: number,
    col: string,
    roof = col,
  ): void =>
    isoBox(x + dx, y + dy, w, d, h * FZ, cover(roof), shade(col, 0.24), shade(col, 0.1), o);
  const disc = (dx: number, dy: number, r: number, col: string): void => {
    const [cx, cy] = project(x + dx, y + dy);
    o.push(
      `<ellipse cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" rx="${(r * TW).toFixed(1)}" ry="${(r * TH).toFixed(1)}" fill="${col}"/>`,
    );
  };
  const pad = { farm: 'farmVerge', compute: 'verge', motorsport: 'grass', arena: 'courtGrass' }[t];
  o.push(`<path d="${polyPath(rectPts(x, y, 1, 1))}" fill="${cover(c(pad))}"/>`);
  if (t === 'farm') {
    if (v === 0) {
      disc(0.5, 0.5, 0.34, snow ? mix(c('pond'), '#DFE8EE', 0.6) : c('pond'));
      for (let i = 0; i < 5; i++)
        box(0.2 + i * 0.07, 0.78, 0.02, 0.02, 0.25, c(snow ? 'stubble' : 'hop'));
    } else if (v === 1) {
      box(0.2, 0.35, 0.3, 0.3, 0.3, c('hay'));
      box(0.6, 0.55, 0.34, 0.2, 0.12, c('timber'));
    } else if (v === 2) {
      box(0.25, 0.3, 0.26, 0.24, 0.3, c('hive'), c('hiveRoof'));
      if (!snow)
        for (let i = 0; i < 6; i++)
          disc(
            0.6 + (i % 3) * 0.1,
            0.65 + Math.floor(i / 3) * 0.1,
            0.03,
            c(['blossom', 'stack', 'saffron'][i % 3]!),
          );
    } else {
      paintTree(x + 0.35, y + 0.4, 11, P, 'wood', o);
      paintTree(x + 0.7, y + 0.7, 9, P, 'wood', o);
    }
  } else if (t === 'compute') {
    if (v === 0) box(0.25, 0.34, 0.5, 0.32, 0.32, c('louvre'), c('white'));
    else if (v === 1) box(0.15, 0.3, 0.7, 0.4, 0.4, c('metal'), c('white'));
    else if (v === 2) {
      box(0.15, 0.2, 0.4, 0.4, 0.2, c('stoneDark'));
      paintTree(x + 0.35, y + 0.4, 8, P, 'wood', o);
      box(0.6, 0.7, 0.32, 0.12, 0.1, c('timber'));
    } else {
      box(0.22, 0.22, 0.56, 0.56, 0.6, c('plantWhite'));
      box(0.22, 0.76, 0.3, 0.02, 0.4, c('trayBlue'));
    }
  } else if (t === 'motorsport') {
    if (v === 0) for (let i = 0; i < 3; i++) box(0.15 + i * 0.25, 0.4, 0.2, 0.2, 0.24, c('tyre'));
    else if (v === 1)
      for (let i = 0; i < 3; i++)
        box(0.1, 0.15 + i * 0.18, 0.8, 0.18, 0.14 * (3 - i), c(i % 2 ? 'white' : 'team'));
    else if (v === 2) box(0.15, 0.48, 0.7, 0.04, 0.56, c('team'));
    else {
      box(0.25, 0.38, 0.5, 0.24, 0.1, c('team'));
      box(0.38, 0.42, 0.2, 0.16, 0.18, c('team'), c('window'));
    }
  } else if (v === 0) {
    box(0.3, 0.3, 0.4, 0.4, 0.2, c('stone'));
    box(0.3, 0.48, 0.04, 0.04, 0.6, c('timberDark'));
    box(0.66, 0.48, 0.04, 0.04, 0.6, c('timberDark'));
  } else if (v === 1) {
    box(0.35, 0.35, 0.3, 0.3, 0.22, c('stoneCap'));
    box(0.44, 0.45, 0.12, 0.1, 0.6, c('stone'));
  } else if (v === 2) {
    disc(0.5, 0.5, 0.2, c('stoneDark'));
    const [cx, cy] = project(x + 0.5, y + 0.5);
    o.push(
      `<path d="M${(cx - 5).toFixed(1)} ${cy.toFixed(1)} L${cx.toFixed(1)} ${(cy - 0.32 * FZ).toFixed(1)} L${(cx + 5).toFixed(1)} ${cy.toFixed(1)}Z" fill="${c('fire')}"/>`,
    );
  } else {
    box(0.47, 0.47, 0.06, 0.06, 0.5, c('timberDark'));
    box(0.42, 0.44, 0.16, 0.12, 0.42, c('hay'));
  }
}
